import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { listBenders } from '@509-bender/bender-library';
import type { CalculatorConfig, InputSpec } from './calculator-configs.js';
import { BenderPicker } from '../components/BenderPicker.js';
import {
  normalizeBenderSelection,
  selectionToShoe,
  type BenderSelection,
} from '../lib/bender-selection.js';
import { ResultBlock } from '../components/ResultBlock.js';
import { BendDiagram } from '../components/BendDiagram.js';
import * as storage from '../lib/storage.js';
import {
  fractionPrecisionToInches,
  getDefaultBenderSelection,
  LAST_BENDER_STORAGE_KEY,
  useAppSettings,
} from '../lib/app-settings.js';
import {
  clearStoredCalculatorValues,
  getStoredCalculatorValues,
  normalizeCalculatorInputValue,
  setStoredCalculatorValues,
} from '../lib/calculator-values.js';
import { formatInches, parseInchesInput } from '../lib/format.js';
import type { LengthDisplayUnit } from '../lib/format.js';
import { lightHaptic, shareText } from '../lib/platform.js';
import type { ShareStatus } from '../lib/platform.js';
import { setLastCalculatorId } from '../lib/recent-calculator.js';
import { buildResultShareText } from '../lib/result-share.js';
import { makeShareUrl } from '../lib/share.js';

interface Props {
  config: CalculatorConfig;
}

type ResultShareStatus = ShareStatus | 'sharing';

export default function CalculatorScreen({ config }: Props) {
  const [values, setValues] = useState<Record<string, number>>(() => savedValuesFor(config));
  const [bender, setBender] = useState<BenderSelection>(() =>
    normalizeBenderSelection(
      storage.get<BenderSelection>(LAST_BENDER_STORAGE_KEY) ?? getDefaultBenderSelection(),
    ),
  );
  const { angleIncrement } = useAppSettings();
  const [showBenderPicker, setShowBenderPicker] = useState(false);
  const [previousAngle, setPreviousAngle] = useState<number | null>(null);
  const [shareStatus, setShareStatus] = useState<ResultShareStatus | null>(null);

  useEffect(() => {
    storage.set(LAST_BENDER_STORAGE_KEY, bender);
  }, [bender]);

  useEffect(() => {
    setValues(savedValuesFor(config));
    setPreviousAngle(null);
    setShowBenderPicker(false);
  }, [config]);

  useEffect(() => {
    setStoredCalculatorValues(config.id, values);
  }, [config.id, values]);

  useEffect(() => {
    setLastCalculatorId(config.id);
  }, [config.id]);

  useEffect(() => {
    setShareStatus(null);
  }, [bender, config.id, values]);

  const shoe = useMemo(
    () => (config.needsBender ? selectionToShoe(bender) : undefined),
    [bender, config.needsBender],
  );

  const { result, error } = useMemo<{
    result: Record<string, unknown> | null;
    error: string | null;
  }>(() => {
    if (config.needsBender && !shoe) {
      return { result: null, error: 'Pick a bender, size, and material that ship together.' };
    }
    try {
      const out = config.calculate(values, shoe ?? undefined);
      return { result: (out ?? null) as Record<string, unknown> | null, error: null };
    } catch (e) {
      return { result: null, error: e instanceof Error ? e.message : String(e) };
    }
  }, [config, values, shoe]);

  function updateValue(spec: InputSpec, next: number) {
    const normalizedNext = normalizeCalculatorInputValue(next, spec);
    const current = values[spec.key] ?? 0;
    if (spec.type.kind === 'angle' && Number.isFinite(current) && current !== normalizedNext) {
      setPreviousAngle(current);
    }
    setValues((prev) => ({ ...prev, [spec.key]: normalizedNext }));
  }

  function toggleBenderPicker() {
    void lightHaptic();
    setShowBenderPicker((next) => !next);
  }

  function resetCalculator() {
    void lightHaptic();
    clearStoredCalculatorValues(config.id);
    setValues(defaultValuesFor(config));
    setPreviousAngle(null);
    setShowBenderPicker(false);
    setShareStatus(null);
  }

  async function shareResult() {
    if (!result) return;
    void lightHaptic();
    setShareStatus('sharing');
    const fields = resultToFields(config.id, result, values, hasVerifiedGain(shoe)).map(
      (field) => ({
        label: field.label,
        value: renderFieldValue(field),
      }),
    );
    const setupContext = config.needsBender
      ? {
          benderLabel: formatBenderLabel(bender),
          conduitLabel: `${bender.conduitSize} ${bender.material}`,
        }
      : undefined;
    const text = buildResultShareText({
      calculatorName: config.displayName,
      ...(setupContext
        ? {
            benderLabel: setupContext.benderLabel,
            conduitLabel: setupContext.conduitLabel,
          }
        : {}),
      fields,
      shareUrl: makeShareUrl({
        kind: 'bend',
        bend: {
          calculatorId: config.id,
          result,
          values,
          ...(config.needsBender ? { bender } : {}),
          ...(setupContext ? { setup: setupContext } : {}),
        },
      }),
    });
    const nextStatus = await shareText(text, `Truebend: ${config.displayName}`);
    setShareStatus(nextStatus);
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 430,
        width: '100%',
        margin: '0 auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          minHeight: 44,
          marginBottom: 'var(--space-3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
          <Link to="/" aria-label="Back to all bends" style={backButtonStyle}>
            <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 22, height: 22 }}>
              <path
                d="M15 5 8 12l7 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
          <h1
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 850,
              lineHeight: 1.1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {config.displayName}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <button
            type="button"
            aria-label="Reset calculator"
            title="Reset calculator"
            onClick={resetCalculator}
            style={resetButtonStyle}
          >
            <ResetIcon />
          </button>
          {config.needsBender && (
            <button
              type="button"
              onClick={toggleBenderPicker}
              aria-expanded={showBenderPicker}
              style={{
                flex: '0 0 auto',
                background: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-pill)',
                color: 'var(--color-text-secondary)',
                padding: 'var(--space-1) var(--space-3)',
                minHeight: 32,
                fontSize: 'var(--text-xs)',
                fontWeight: 800,
              }}
            >
              {bender.conduitSize} {bender.material}
            </button>
          )}
        </div>
      </div>

      {config.needsBender && showBenderPicker && (
        <BenderPicker value={bender} onChange={setBender} />
      )}

      {config.id === 'offset' ? (
        <OffsetFastLayout
          config={config}
          values={values}
          bender={bender}
          result={result}
          error={error}
          angleIncrement={angleIncrement}
          onToggleBender={toggleBenderPicker}
          onChange={updateValue}
          onShareResult={shareResult}
          shareStatus={shareStatus}
        />
      ) : config.id === 'ninety' ? (
        <NinetyFastLayout
          config={config}
          values={values}
          bender={bender}
          result={result}
          error={error}
          angleIncrement={angleIncrement}
          onToggleBender={toggleBenderPicker}
          onChange={updateValue}
          onShareResult={shareResult}
          shareStatus={shareStatus}
        />
      ) : config.id === 'back-to-back' ? (
        <BackToBackFastLayout
          config={config}
          values={values}
          bender={bender}
          result={result}
          error={error}
          angleIncrement={angleIncrement}
          onToggleBender={toggleBenderPicker}
          onChange={updateValue}
          onShareResult={shareResult}
          shareStatus={shareStatus}
        />
      ) : config.id === 'saddle3' ? (
        <Saddle3FastLayout
          config={config}
          values={values}
          bender={bender}
          result={result}
          error={error}
          angleIncrement={angleIncrement}
          onToggleBender={toggleBenderPicker}
          onChange={updateValue}
          onShareResult={shareResult}
          shareStatus={shareStatus}
        />
      ) : config.id === 'saddle4' ? (
        <Saddle4FastLayout
          config={config}
          values={values}
          bender={bender}
          result={result}
          error={error}
          angleIncrement={angleIncrement}
          onToggleBender={toggleBenderPicker}
          onChange={updateValue}
          onShareResult={shareResult}
          shareStatus={shareStatus}
        />
      ) : config.id === 'kick' ? (
        <KickFastLayout
          config={config}
          values={values}
          bender={bender}
          result={result}
          error={error}
          angleIncrement={angleIncrement}
          onToggleBender={toggleBenderPicker}
          onChange={updateValue}
          onShareResult={shareResult}
          shareStatus={shareStatus}
        />
      ) : config.id === 'rolling-offset' ? (
        <RollingOffsetFastLayout
          config={config}
          values={values}
          bender={bender}
          result={result}
          error={error}
          angleIncrement={angleIncrement}
          onToggleBender={toggleBenderPicker}
          onChange={updateValue}
          onShareResult={shareResult}
          shareStatus={shareStatus}
        />
      ) : config.id === 'parallel-offset' ? (
        <ParallelOffsetFastLayout
          config={config}
          values={values}
          bender={bender}
          result={result}
          error={error}
          angleIncrement={angleIncrement}
          onToggleBender={toggleBenderPicker}
          onChange={updateValue}
          onShareResult={shareResult}
          shareStatus={shareStatus}
        />
      ) : config.id === 'concentric' ? (
        <ConcentricFastLayout
          config={config}
          values={values}
          bender={bender}
          result={result}
          error={error}
          angleIncrement={angleIncrement}
          onToggleBender={toggleBenderPicker}
          onChange={updateValue}
          onShareResult={shareResult}
          shareStatus={shareStatus}
        />
      ) : config.id === 'segmented' ? (
        <SegmentedFastLayout
          config={config}
          values={values}
          bender={bender}
          result={result}
          error={error}
          angleIncrement={angleIncrement}
          onToggleBender={toggleBenderPicker}
          onChange={updateValue}
          onShareResult={shareResult}
          shareStatus={shareStatus}
        />
      ) : (
        <>
          {config.needsBender && (
            <BenderSelectorRow bender={bender} onToggleBender={toggleBenderPicker} />
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 'var(--space-2)',
              marginBottom: 'var(--space-4)',
            }}
          >
            {config.inputs.map((inp) => (
              <InputField
                key={inp.key}
                spec={inp}
                value={values[inp.key] ?? 0}
                angleIncrement={angleIncrement}
                onChange={(v) => updateValue(inp, v)}
              />
            ))}
          </div>

          <CalculatorError error={error} />

          {result && (
            <>
              <ResultBlock
                fields={resultToFields(config.id, result, values, hasVerifiedGain(shoe))}
              />
              <GenericAngleLevelPanel config={config} values={values} />
              <ResultActionBar onShare={shareResult} status={shareStatus} />
              <BendDiagram
                type={config.id}
                result={result}
                inputs={values}
                previousAngle={previousAngle ?? undefined}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

function defaultValuesFor(config: CalculatorConfig): Record<string, number> {
  const init: Record<string, number> = {};
  for (const inp of config.inputs) init[inp.key] = inp.default;
  return init;
}

function savedValuesFor(config: CalculatorConfig): Record<string, number> {
  return getStoredCalculatorValues(config.id, defaultValuesFor(config), config.inputs);
}

interface InputFieldProps {
  spec: InputSpec;
  value: number;
  angleIncrement: number;
  onChange: (v: number) => void;
}

interface OffsetFastLayoutProps {
  config: CalculatorConfig;
  values: Record<string, number>;
  bender: BenderSelection;
  result: Record<string, unknown> | null;
  error: string | null;
  angleIncrement: number;
  onToggleBender: () => void;
  onChange: (spec: InputSpec, value: number) => void;
  onShareResult: () => void;
  shareStatus: ResultShareStatus | null;
}

function BenderSelectorRow({
  bender,
  onToggleBender,
}: {
  bender: BenderSelection;
  onToggleBender: () => void;
}) {
  return (
    <div style={offsetSelectorRowStyle}>
      <button type="button" onClick={onToggleBender} style={offsetChipButtonStyle}>
        <span style={offsetChipLabelStyle}>Bender</span>
        <span style={offsetChipValueStyle}>{formatBenderLabel(bender)}</span>
      </button>
      <button type="button" onClick={onToggleBender} style={offsetChipButtonStyle}>
        <span style={offsetChipLabelStyle}>Conduit</span>
        <span style={offsetChipValueStyle}>{bender.material}</span>
      </button>
      <button type="button" onClick={onToggleBender} style={offsetChipButtonStyle}>
        <span style={offsetChipLabelStyle}>Size</span>
        <span style={offsetChipValueStyle}>{bender.conduitSize}"</span>
      </button>
    </div>
  );
}

function NinetyFastLayout({
  config,
  values,
  bender,
  result,
  error,
  angleIncrement,
  onToggleBender,
  onChange,
  onShareResult,
  shareStatus,
}: OffsetFastLayoutProps) {
  const stubSpec = config.inputs.find((spec) => spec.key === 'stubHeight');
  const resultRecord = (result ?? {}) as Record<string, number>;
  const stubHeight = values.stubHeight ?? 0;
  const markFromEnd = Number(resultRecord.markFromEnd ?? 0);
  const totalLength = Number(resultRecord.totalLength ?? 0);
  const shoe = selectionToShoe(bender);
  const gainVerified = hasVerifiedGain(shoe);

  return (
    <section style={offsetShellStyle} aria-label="Fast 90 degree stub-up calculator">
      <BenderSelectorRow bender={bender} onToggleBender={onToggleBender} />

      {stubSpec && (
        <InputField
          spec={stubSpec}
          value={stubHeight}
          angleIncrement={angleIncrement}
          onChange={(next) => onChange(stubSpec, next)}
        />
      )}

      <CalculatorError error={error} />

      {result && (
        <>
          <NinetyHeroDiagram
            stubHeight={stubHeight}
            markFromEnd={markFromEnd}
            takeUp={shoe?.takeUp ?? 0}
          />
          <div style={offsetResultGridStyle}>
            <OffsetResultCard label="Mark at" value={formatInches(markFromEnd)} featured />
            <OffsetResultCard label="Take-up" value={formatInches(shoe?.takeUp ?? 0)} />
            <OffsetResultCard
              label={gainBasedLengthLabel('Cut length', gainVerified)}
              value={formatInches(totalLength)}
            />
            <OffsetResultCard label="Stub" value={formatInches(stubHeight)} />
          </div>
          <GainEstimateNote gainVerified={gainVerified} />
          <div style={offsetAnglePanelStyle}>
            <div style={saddleAngleHelperStyle}>
              <span>Bend: {formatAngle(90)}</span>
              <Link
                to={levelTargetPath(90, '/ninety')}
                aria-label="Open level for 90° stub-up bend"
                style={angleLevelLinkStyle}
              >
                Level
              </Link>
            </div>
          </div>
          <ResultActionBar onShare={onShareResult} status={shareStatus} />
        </>
      )}
    </section>
  );
}

function BackToBackFastLayout({
  config,
  values,
  bender,
  result,
  error,
  angleIncrement,
  onToggleBender,
  onChange,
  onShareResult,
  shareStatus,
}: OffsetFastLayoutProps) {
  const stub1Spec = config.inputs.find((spec) => spec.key === 'stub1');
  const betweenSpec = config.inputs.find((spec) => spec.key === 'betweenStubs');
  const stub2Spec = config.inputs.find((spec) => spec.key === 'stub2');
  const resultRecord = (result ?? {}) as Record<string, number>;
  const stub1 = values.stub1 ?? 0;
  const betweenStubs = values.betweenStubs ?? 0;
  const stub2 = values.stub2 ?? 0;
  const firstMark = Number(resultRecord.firstMark ?? 0);
  const secondMark = Number(resultRecord.secondMark ?? 0);
  const totalLength = Number(resultRecord.totalLength ?? 0);
  const shoe = selectionToShoe(bender);
  const gainVerified = hasVerifiedGain(shoe);

  return (
    <section style={offsetShellStyle} aria-label="Fast back-to-back 90 calculator">
      <BenderSelectorRow bender={bender} onToggleBender={onToggleBender} />

      <div style={offsetQuickInputGridStyle}>
        {stub1Spec && (
          <InputField
            spec={stub1Spec}
            value={stub1}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(stub1Spec, next)}
          />
        )}
        {betweenSpec && (
          <InputField
            spec={betweenSpec}
            value={betweenStubs}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(betweenSpec, next)}
          />
        )}
        {stub2Spec && (
          <InputField
            spec={stub2Spec}
            value={stub2}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(stub2Spec, next)}
          />
        )}
      </div>

      <CalculatorError error={error} />

      {result && (
        <>
          <BackToBackHeroDiagram
            stub1={stub1}
            stub2={stub2}
            betweenStubs={betweenStubs}
            firstMark={firstMark}
            secondMark={secondMark}
          />
          <ResultBlock fields={resultToFields(config.id, result, values, gainVerified)} />
          <BackToBackSetup
            firstMark={firstMark}
            secondMark={secondMark}
            betweenStubs={betweenStubs}
            takeUp={shoe?.takeUp ?? 0}
            totalLength={totalLength}
            gainVerified={gainVerified}
          />
          <ResultActionBar onShare={onShareResult} status={shareStatus} />
        </>
      )}

      <div style={offsetAnglePanelStyle}>
        <div style={saddleAngleHelperStyle}>
          <span>Bends: {formatAngle(90)} each</span>
          <Link
            to={levelTargetPath(90, '/back-to-back')}
            aria-label="Open level for 90° back-to-back bends"
            style={angleLevelLinkStyle}
          >
            Level
          </Link>
        </div>
      </div>
    </section>
  );
}

function GenericAngleLevelPanel({
  config,
  values,
}: {
  config: CalculatorConfig;
  values: Record<string, number>;
}) {
  const targets = getLevelTargets(config, values);
  if (targets.length === 0) return null;

  return (
    <div style={offsetAnglePanelStyle}>
      {targets.map((target, index) => (
        <div
          key={target.key}
          style={{
            ...saddleAngleHelperStyle,
            marginBottom: index === targets.length - 1 ? 0 : 'var(--space-2)',
          }}
        >
          <span>
            {target.label}: {formatAngle(target.angle)}
            {target.suffix ?? ''}
          </span>
          <Link
            to={levelTargetPath(target.angle, `/${config.id}`)}
            aria-label={`Open level for ${formatAngle(target.angle)} ${target.accessibleLabel}`}
            style={angleLevelLinkStyle}
          >
            Level
          </Link>
        </div>
      ))}
    </div>
  );
}

interface LevelTarget {
  key: string;
  label: string;
  accessibleLabel: string;
  angle: number;
  suffix?: string;
}

function getLevelTargets(config: CalculatorConfig, values: Record<string, number>): LevelTarget[] {
  if (config.id === 'back-to-back') {
    return [
      {
        key: 'back-to-back-90',
        label: 'Bends',
        accessibleLabel: 'back-to-back bends',
        angle: 90,
        suffix: ' each',
      },
    ];
  }

  if (config.id === 'segmented') {
    const segmentSpec = config.inputs.find((spec) => spec.key === 'segmentAngle');
    const angle = segmentSpec ? (values[segmentSpec.key] ?? segmentSpec.default) : NaN;
    return Number.isFinite(angle)
      ? [
          {
            key: 'segmentAngle',
            label: 'Each bend',
            accessibleLabel: 'angle per segment',
            angle,
          },
        ]
      : [];
  }

  return config.inputs.flatMap((spec) => {
    if (!isLevelAngleSpec(spec)) return [];
    const angle = values[spec.key] ?? spec.default;
    if (!Number.isFinite(angle)) return [];
    return [
      {
        key: spec.key,
        label: spec.label,
        accessibleLabel: spec.label.toLowerCase(),
        angle,
      },
    ];
  });
}

function isLevelAngleSpec(spec: InputSpec): boolean {
  return spec.type.kind === 'angle' || (spec.type.kind === 'number' && spec.type.unit === '\u00b0');
}

function Saddle3FastLayout({
  config,
  values,
  bender,
  result,
  error,
  angleIncrement,
  onToggleBender,
  onChange,
  onShareResult,
  shareStatus,
}: OffsetFastLayoutProps) {
  const heightSpec = config.inputs.find((spec) => spec.key === 'obstructionHeight');
  const centerSpec = config.inputs.find((spec) => spec.key === 'centerMark');
  const angleSpec = config.inputs.find((spec) => spec.key === 'angle');
  const resultRecord = (result ?? {}) as Record<string, number>;
  const obstructionHeight = values.obstructionHeight ?? 0;
  const centerMark = Number(resultRecord.centerMark ?? values.centerMark ?? 0);
  const firstSideMark = Number(resultRecord.firstSideMark ?? centerMark);
  const secondSideMark = Number(resultRecord.secondSideMark ?? centerMark);
  const centerAngle = Number(resultRecord.centerAngle ?? values.angle ?? 45);
  const sideBendAngle = Number(resultRecord.sideBendAngle ?? centerAngle / 2);
  const shrink = Number(resultRecord.shrink ?? 0);

  return (
    <section style={offsetShellStyle} aria-label="Fast 3-point saddle calculator">
      <BenderSelectorRow bender={bender} onToggleBender={onToggleBender} />

      <div style={offsetQuickInputGridStyle}>
        {heightSpec && (
          <InputField
            spec={heightSpec}
            value={obstructionHeight}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(heightSpec, next)}
          />
        )}
        {centerSpec && (
          <InputField
            spec={centerSpec}
            value={values.centerMark ?? 0}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(centerSpec, next)}
          />
        )}
      </div>

      <CalculatorError error={error} />

      {result && (
        <>
          <Saddle3HeroDiagram
            obstructionHeight={obstructionHeight}
            firstSideMark={firstSideMark}
            centerMark={centerMark}
            secondSideMark={secondSideMark}
            centerAngle={centerAngle}
            sideBendAngle={sideBendAngle}
            shrink={shrink}
          />
          <div style={offsetResultGridStyle}>
            <OffsetResultCard label="Center mark" value={formatInches(centerMark)} featured />
            <OffsetResultCard label="1st mark" value={formatInches(firstSideMark)} />
            <OffsetResultCard label="3rd mark" value={formatInches(secondSideMark)} />
            <OffsetResultCard label="Center bend" value={formatAngle(centerAngle)} />
            <OffsetResultCard label="Side bends" value={formatAngle(sideBendAngle)} />
            <OffsetResultCard label="Shrink" value={formatInches(shrink)} />
          </div>
          <ResultActionBar onShare={onShareResult} status={shareStatus} />
        </>
      )}

      {angleSpec && (
        <div style={offsetAnglePanelStyle}>
          <div style={saddleAngleHelperStyle}>
            <span>Side bends: {formatAngle(sideBendAngle)} each</span>
            <Link
              to={levelTargetPath(sideBendAngle, '/saddle3')}
              aria-label={`Open level for ${formatAngle(sideBendAngle)} side bends`}
              style={angleLevelLinkStyle}
            >
              Level
            </Link>
          </div>
          <InputField
            spec={angleSpec}
            value={values.angle ?? 45}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(angleSpec, next)}
          />
        </div>
      )}
    </section>
  );
}

function Saddle4FastLayout({
  config,
  values,
  bender,
  result,
  error,
  angleIncrement,
  onToggleBender,
  onChange,
  onShareResult,
  shareStatus,
}: OffsetFastLayoutProps) {
  const heightSpec = config.inputs.find((spec) => spec.key === 'obstructionHeight');
  const widthSpec = config.inputs.find((spec) => spec.key === 'obstructionWidth');
  const angleSpec = config.inputs.find((spec) => spec.key === 'angle');
  const startSpec = config.inputs.find((spec) => spec.key === 'startMark');
  const resultRecord = (result ?? {}) as Record<string, unknown>;
  const marks = Array.isArray(resultRecord.marks)
    ? resultRecord.marks.map((mark) => Number(mark))
    : [];
  const [firstMark = 0, secondMark = 0, thirdMark = 0, fourthMark = 0] = marks;
  const obstructionHeight = values.obstructionHeight ?? 0;
  const obstructionWidth = values.obstructionWidth ?? 0;
  const startMark = values.startMark ?? 0;
  const angle = values.angle ?? 30;
  const offsetSpacing = secondMark - firstMark;
  const acrossTop = thirdMark - secondMark;
  const shrink = Number(resultRecord.shrink ?? 0);
  const totalLength = Number(resultRecord.totalLength ?? 0);
  const gainVerified = hasVerifiedGain(selectionToShoe(bender));

  return (
    <section style={offsetShellStyle} aria-label="Fast 4-point saddle calculator">
      <BenderSelectorRow bender={bender} onToggleBender={onToggleBender} />

      <div style={offsetQuickInputGridStyle}>
        {heightSpec && (
          <InputField
            spec={heightSpec}
            value={obstructionHeight}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(heightSpec, next)}
          />
        )}
        {widthSpec && (
          <InputField
            spec={widthSpec}
            value={obstructionWidth}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(widthSpec, next)}
          />
        )}
        {startSpec && (
          <InputField
            spec={startSpec}
            value={startMark}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(startSpec, next)}
          />
        )}
      </div>

      <CalculatorError error={error} />

      {result && (
        <>
          <Saddle4HeroDiagram
            obstructionHeight={obstructionHeight}
            obstructionWidth={obstructionWidth}
            angle={angle}
            firstMark={firstMark}
            secondMark={secondMark}
            thirdMark={thirdMark}
            fourthMark={fourthMark}
            offsetSpacing={offsetSpacing}
          />
          <ResultBlock fields={resultToFields(config.id, result, values, gainVerified)} />
          <Saddle4Setup
            firstMark={firstMark}
            secondMark={secondMark}
            thirdMark={thirdMark}
            fourthMark={fourthMark}
            offsetSpacing={offsetSpacing}
            acrossTop={acrossTop}
            shrink={shrink}
            totalLength={totalLength}
            gainVerified={gainVerified}
          />
          <ResultActionBar onShare={onShareResult} status={shareStatus} />
        </>
      )}

      {angleSpec && (
        <div style={offsetAnglePanelStyle}>
          <div style={saddleAngleHelperStyle}>
            <span>Bends: {formatAngle(angle)} each</span>
            <Link
              to={levelTargetPath(angle, '/saddle4')}
              aria-label={`Open level for ${formatAngle(angle)} 4-point saddle bends`}
              style={angleLevelLinkStyle}
            >
              Level
            </Link>
          </div>
          <InputField
            spec={angleSpec}
            value={angle}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(angleSpec, next)}
          />
        </div>
      )}
    </section>
  );
}

function OffsetFastLayout({
  config,
  values,
  bender,
  result,
  error,
  angleIncrement,
  onToggleBender,
  onChange,
  onShareResult,
  shareStatus,
}: OffsetFastLayoutProps) {
  const riseSpec = config.inputs.find((spec) => spec.key === 'rise');
  const fromEndSpec = config.inputs.find((spec) => spec.key === 'fromEnd');
  const angleSpec = config.inputs.find((spec) => spec.key === 'angle');
  const resultRecord = (result ?? {}) as Record<string, number>;
  const distance = Number(resultRecord.distanceBetweenMarks ?? 0);
  const shrink = Number(resultRecord.shrink ?? 0);
  const rise = values.rise ?? 0;
  const fromEnd = values.fromEnd ?? 0;
  const angle = values.angle ?? 30;

  return (
    <section style={offsetShellStyle} aria-label="Fast offset calculator">
      <BenderSelectorRow bender={bender} onToggleBender={onToggleBender} />

      <div style={offsetQuickInputGridStyle}>
        {riseSpec && (
          <InputField
            spec={riseSpec}
            value={rise}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(riseSpec, next)}
          />
        )}
        {fromEndSpec && (
          <InputField
            spec={fromEndSpec}
            value={fromEnd}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(fromEndSpec, next)}
          />
        )}
      </div>

      <CalculatorError error={error} />

      {result && (
        <>
          <OffsetHeroDiagram
            rise={rise}
            angle={angle}
            distance={distance}
            shrink={shrink}
            fromEnd={fromEnd}
          />
          <div style={offsetResultGridStyle}>
            <OffsetResultCard label="Between bends" value={formatInches(distance)} featured />
            <OffsetResultCard label="Bend" value={formatAngle(angle)} />
            <OffsetResultCard label="Mark 1" value={formatInches(fromEnd)} />
            <OffsetResultCard label="Mark 2" value={formatInches(fromEnd + distance)} />
            <OffsetResultCard label="Shrink" value={formatInches(shrink)} />
          </div>
          <ResultActionBar onShare={onShareResult} status={shareStatus} />
        </>
      )}

      {angleSpec && (
        <div style={offsetAnglePanelStyle}>
          <div style={saddleAngleHelperStyle}>
            <span>Bend: {formatAngle(angle)}</span>
            <Link
              to={levelTargetPath(angle, '/offset')}
              aria-label={`Open level for ${formatAngle(angle)} bend`}
              style={angleLevelLinkStyle}
            >
              Level
            </Link>
            <Link
              to={squareAlignPath('/offset', 90)}
              aria-label="Open Square Align for offset layout"
              style={squareAlignLinkStyle}
            >
              Square
            </Link>
          </div>
          <InputField
            spec={angleSpec}
            value={angle}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(angleSpec, next)}
          />
        </div>
      )}
    </section>
  );
}

function KickFastLayout({
  config,
  values,
  bender,
  result,
  error,
  angleIncrement,
  onToggleBender,
  onChange,
  onShareResult,
  shareStatus,
}: OffsetFastLayoutProps) {
  const stubSpec = config.inputs.find((spec) => spec.key === 'stubHeight');
  const kickOffsetSpec = config.inputs.find((spec) => spec.key === 'kickOffset');
  const angleSpec = config.inputs.find((spec) => spec.key === 'kickAngle');
  const resultRecord = (result ?? {}) as Record<string, number>;
  const stubHeight = values.stubHeight ?? 0;
  const kickOffset = values.kickOffset ?? 0;
  const kickAngle = values.kickAngle ?? 30;
  const ninetyMark = Number(resultRecord.ninetyMark ?? 0);
  const kickMarkDistance = Number(resultRecord.kickMarkDistance ?? 0);
  const shrink = Number(resultRecord.shrink ?? 0);
  const totalLength = Number(resultRecord.totalLength ?? 0);
  const gainVerified = hasVerifiedGain(selectionToShoe(bender));

  return (
    <section style={offsetShellStyle} aria-label="Fast kick calculator">
      <BenderSelectorRow bender={bender} onToggleBender={onToggleBender} />

      <div style={offsetQuickInputGridStyle}>
        {stubSpec && (
          <InputField
            spec={stubSpec}
            value={stubHeight}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(stubSpec, next)}
          />
        )}
        {kickOffsetSpec && (
          <InputField
            spec={kickOffsetSpec}
            value={kickOffset}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(kickOffsetSpec, next)}
          />
        )}
      </div>

      <CalculatorError error={error} />

      {result && (
        <>
          <KickHeroDiagram
            stubHeight={stubHeight}
            kickOffset={kickOffset}
            kickAngle={kickAngle}
            ninetyMark={ninetyMark}
            kickMarkDistance={kickMarkDistance}
          />
          <ResultBlock fields={resultToFields(config.id, result, values, gainVerified)} />
          <KickSetup
            ninetyMark={ninetyMark}
            kickMarkDistance={kickMarkDistance}
            shrink={shrink}
            totalLength={totalLength}
            gainVerified={gainVerified}
          />
          <ResultActionBar onShare={onShareResult} status={shareStatus} />
        </>
      )}

      {angleSpec && (
        <div style={offsetAnglePanelStyle}>
          <div style={saddleAngleHelperStyle}>
            <span>Kick angle: {formatAngle(kickAngle)}</span>
            <Link
              to={levelTargetPath(kickAngle, '/kick')}
              aria-label={`Open level for ${formatAngle(kickAngle)} kick angle`}
              style={angleLevelLinkStyle}
            >
              Level
            </Link>
          </div>
          <InputField
            spec={angleSpec}
            value={kickAngle}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(angleSpec, next)}
          />
        </div>
      )}
    </section>
  );
}

function RollingOffsetFastLayout({
  config,
  values,
  bender,
  result,
  error,
  angleIncrement,
  onToggleBender,
  onChange,
  onShareResult,
  shareStatus,
}: OffsetFastLayoutProps) {
  const riseSpec = config.inputs.find((spec) => spec.key === 'rise');
  const rollSpec = config.inputs.find((spec) => spec.key === 'roll');
  const angleSpec = config.inputs.find((spec) => spec.key === 'angle');
  const resultRecord = (result ?? {}) as Record<string, number>;
  const rise = values.rise ?? 0;
  const roll = values.roll ?? 0;
  const angle = values.angle ?? 30;
  const trueOffset = Number(resultRecord.trueOffset ?? 0);
  const rollAngle = Number(resultRecord.rollAngle ?? 0);
  const distance = Number(resultRecord.distanceBetweenMarks ?? 0);
  const shrink = Number(resultRecord.shrink ?? 0);

  return (
    <section style={offsetShellStyle} aria-label="Fast rolling offset calculator">
      <BenderSelectorRow bender={bender} onToggleBender={onToggleBender} />

      <div style={offsetQuickInputGridStyle}>
        {riseSpec && (
          <InputField
            spec={riseSpec}
            value={rise}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(riseSpec, next)}
          />
        )}
        {rollSpec && (
          <InputField
            spec={rollSpec}
            value={roll}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(rollSpec, next)}
          />
        )}
      </div>

      <CalculatorError error={error} />

      {result && (
        <>
          <RollingOffsetHeroDiagram
            rise={rise}
            roll={roll}
            angle={angle}
            trueOffset={trueOffset}
            rollAngle={rollAngle}
            distance={distance}
          />
          <ResultBlock fields={resultToFields(config.id, result, values)} />
          <RollingOffsetSetup
            rollAngle={rollAngle}
            trueOffset={trueOffset}
            distance={distance}
            shrink={shrink}
          />
          <ResultActionBar onShare={onShareResult} status={shareStatus} />
        </>
      )}

      {angleSpec && (
        <div style={offsetAnglePanelStyle}>
          <div style={saddleAngleHelperStyle}>
            <span>Bend: {formatAngle(angle)}</span>
            <Link
              to={levelTargetPath(angle, '/rolling-offset')}
              aria-label={`Open level for ${formatAngle(angle)} rolling offset bends`}
              style={angleLevelLinkStyle}
            >
              Level
            </Link>
          </div>
          <InputField
            spec={angleSpec}
            value={angle}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(angleSpec, next)}
          />
        </div>
      )}
    </section>
  );
}

function ParallelOffsetFastLayout({
  config,
  values,
  bender,
  result,
  error,
  angleIncrement,
  onToggleBender,
  onChange,
  onShareResult,
  shareStatus,
}: OffsetFastLayoutProps) {
  const riseSpec = config.inputs.find((spec) => spec.key === 'rise');
  const fromEndSpec = config.inputs.find((spec) => spec.key === 'fromEnd');
  const angleSpec = config.inputs.find((spec) => spec.key === 'angle');
  const spacingSpec = config.inputs.find((spec) => spec.key === 'spacing');
  const conduitCountSpec = config.inputs.find((spec) => spec.key === 'conduitCount');
  const resultRecord = (result ?? {}) as Record<string, unknown>;
  const runs = parallelRunsFromResult(resultRecord);
  const rise = values.rise ?? 0;
  const fromEnd = values.fromEnd ?? 0;
  const angle = values.angle ?? 30;
  const spacing = values.spacing ?? 0;
  const conduitCount = values.conduitCount ?? runs.length;
  const progression = Number(resultRecord.progression ?? 0);
  const distance = Number(resultRecord.distanceBetweenMarks ?? 0);
  const shrink = Number(resultRecord.shrink ?? 0);

  return (
    <section style={offsetShellStyle} aria-label="Fast parallel offset calculator">
      <BenderSelectorRow bender={bender} onToggleBender={onToggleBender} />

      <div style={offsetQuickInputGridStyle}>
        {riseSpec && (
          <InputField
            spec={riseSpec}
            value={rise}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(riseSpec, next)}
          />
        )}
        {fromEndSpec && (
          <InputField
            spec={fromEndSpec}
            value={fromEnd}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(fromEndSpec, next)}
          />
        )}
      </div>

      <CalculatorError error={error} />

      {result && (
        <>
          <ParallelOffsetHeroDiagram
            rise={rise}
            angle={angle}
            progression={progression}
            distance={distance}
            runs={runs}
          />
          <ResultBlock fields={resultToFields(config.id, result, values)} />
          <ParallelRunMarks
            runs={runs}
            progression={progression}
            distance={distance}
            shrink={shrink}
          />
          <ResultActionBar onShare={onShareResult} status={shareStatus} />
        </>
      )}

      <div style={offsetAnglePanelStyle}>
        {angleSpec && (
          <>
            <div style={saddleAngleHelperStyle}>
              <span>Bend: {formatAngle(angle)}</span>
              <Link
                to={levelTargetPath(angle, '/parallel-offset')}
                aria-label={`Open level for ${formatAngle(angle)} parallel offset bends`}
                style={angleLevelLinkStyle}
              >
                Level
              </Link>
              <Link
                to={squareAlignPath('/parallel-offset', 90)}
                aria-label="Open Square Align for parallel offset layout"
                style={squareAlignLinkStyle}
              >
                Square
              </Link>
            </div>
            <InputField
              spec={angleSpec}
              value={angle}
              angleIncrement={angleIncrement}
              onChange={(next) => onChange(angleSpec, next)}
            />
          </>
        )}
        <div style={parallelSecondaryInputGridStyle}>
          {spacingSpec && (
            <InputField
              spec={spacingSpec}
              value={spacing}
              angleIncrement={angleIncrement}
              onChange={(next) => onChange(spacingSpec, next)}
            />
          )}
          {conduitCountSpec && (
            <InputField
              spec={conduitCountSpec}
              value={conduitCount}
              angleIncrement={angleIncrement}
              onChange={(next) => onChange(conduitCountSpec, next)}
            />
          )}
        </div>
      </div>
    </section>
  );
}

function ConcentricFastLayout({
  config,
  values,
  result,
  error,
  angleIncrement,
  onChange,
  onShareResult,
  shareStatus,
}: OffsetFastLayoutProps) {
  const radiusSpec = config.inputs.find((spec) => spec.key === 'innerRadius');
  const spacingSpec = config.inputs.find((spec) => spec.key === 'spacing');
  const angleSpec = config.inputs.find((spec) => spec.key === 'angle');
  const countSpec = config.inputs.find((spec) => spec.key === 'parallelCount');
  const resultRecord = (result ?? {}) as Record<string, unknown>;
  const runs = concentricRunsFromResult(resultRecord);
  const innerRadius = values.innerRadius ?? 0;
  const spacing = values.spacing ?? 0;
  const angle = values.angle ?? 90;
  const parallelCount = values.parallelCount ?? runs.length;

  return (
    <section style={offsetShellStyle} aria-label="Fast concentric calculator">
      <div style={offsetQuickInputGridStyle}>
        {radiusSpec && (
          <InputField
            spec={radiusSpec}
            value={innerRadius}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(radiusSpec, next)}
          />
        )}
        {spacingSpec && (
          <InputField
            spec={spacingSpec}
            value={spacing}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(spacingSpec, next)}
          />
        )}
      </div>

      <CalculatorError error={error} />

      {result && (
        <>
          <ConcentricHeroDiagram
            innerRadius={innerRadius}
            spacing={spacing}
            angle={angle}
            runs={runs}
          />
          <ResultBlock fields={resultToFields(config.id, result, values)} />
          <ConcentricRunAddOns runs={runs} spacing={spacing} angle={angle} />
          <ResultActionBar onShare={onShareResult} status={shareStatus} />
        </>
      )}

      <div style={offsetAnglePanelStyle}>
        {angleSpec && (
          <>
            <div style={saddleAngleHelperStyle}>
              <span>Bend: {formatAngle(angle)}</span>
              <Link
                to={levelTargetPath(angle, '/concentric')}
                aria-label={`Open level for ${formatAngle(angle)} concentric bends`}
                style={angleLevelLinkStyle}
              >
                Level
              </Link>
              <Link
                to={squareAlignPath('/concentric', 90)}
                aria-label="Open Square Align for concentric layout"
                style={squareAlignLinkStyle}
              >
                Square
              </Link>
            </div>
            <InputField
              spec={angleSpec}
              value={angle}
              angleIncrement={angleIncrement}
              onChange={(next) => onChange(angleSpec, next)}
            />
          </>
        )}
        {countSpec && (
          <div style={parallelSecondaryInputGridStyle}>
            <InputField
              spec={countSpec}
              value={parallelCount}
              angleIncrement={angleIncrement}
              onChange={(next) => onChange(countSpec, next)}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function SegmentedFastLayout({
  config,
  values,
  result,
  error,
  angleIncrement,
  onChange,
  onShareResult,
  shareStatus,
}: OffsetFastLayoutProps) {
  const radiusSpec = config.inputs.find((spec) => spec.key === 'totalRadius');
  const totalAngleSpec = config.inputs.find((spec) => spec.key === 'totalAngle');
  const segmentAngleSpec = config.inputs.find((spec) => spec.key === 'segmentAngle');
  const resultRecord = (result ?? {}) as Record<string, number>;
  const totalRadius = values.totalRadius ?? 0;
  const totalAngle = values.totalAngle ?? 90;
  const segmentAngle = values.segmentAngle ?? 5;
  const segmentCount = Number(resultRecord.segmentCount ?? 0);
  const markSpacing = Number(resultRecord.markSpacing ?? resultRecord.chordLength ?? 0);
  const totalLength = Number(resultRecord.totalLength ?? 0);

  return (
    <section style={offsetShellStyle} aria-label="Fast segmented calculator">
      {radiusSpec && (
        <InputField
          spec={radiusSpec}
          value={totalRadius}
          angleIncrement={angleIncrement}
          onChange={(next) => onChange(radiusSpec, next)}
        />
      )}

      <CalculatorError error={error} />

      {result && (
        <>
          <SegmentedHeroDiagram
            totalRadius={totalRadius}
            totalAngle={totalAngle}
            segmentAngle={segmentAngle}
            segmentCount={segmentCount}
            markSpacing={markSpacing}
          />
          <ResultBlock fields={resultToFields(config.id, result, values)} />
          <SegmentedSetup
            segmentCount={segmentCount}
            markSpacing={markSpacing}
            segmentAngle={segmentAngle}
            totalLength={totalLength}
            totalRadius={totalRadius}
          />
          <ResultActionBar onShare={onShareResult} status={shareStatus} />
        </>
      )}

      <div style={offsetAnglePanelStyle}>
        {totalAngleSpec && (
          <InputField
            spec={totalAngleSpec}
            value={totalAngle}
            angleIncrement={angleIncrement}
            onChange={(next) => onChange(totalAngleSpec, next)}
          />
        )}
        {segmentAngleSpec && (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <div style={saddleAngleHelperStyle}>
              <span>Each bend: {formatAngle(segmentAngle)}</span>
              <Link
                to={levelTargetPath(segmentAngle, '/segmented')}
                aria-label={`Open level for ${formatAngle(segmentAngle)} angle per segment`}
                style={angleLevelLinkStyle}
              >
                Level
              </Link>
            </div>
            <InputField
              spec={segmentAngleSpec}
              value={segmentAngle}
              angleIncrement={angleIncrement}
              onChange={(next) => onChange(segmentAngleSpec, next)}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function CalculatorError({ error }: { error: string | null }) {
  if (!error) return null;
  // Errors may be a single line, or "Headline\nReadable detail" — render the
  // headline in alert red and the detail in quieter, more legible secondary text.
  const [title, ...rest] = error.split('\n');
  const detail = rest.join(' ').trim();
  return (
    <div
      role="alert"
      style={{
        marginTop: 'var(--space-4)',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--color-surface)',
        borderLeft: '4px solid var(--color-error)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--color-error)',
        fontSize: 'var(--text-sm)',
        lineHeight: 1.4,
      }}
    >
      <span style={{ display: 'block', fontWeight: 850 }}>{title}</span>
      {detail && (
        <span
          style={{
            display: 'block',
            marginTop: 2,
            color: 'var(--color-text-secondary)',
            fontWeight: 500,
          }}
        >
          {detail}
        </span>
      )}
    </div>
  );
}

function ResultActionBar({
  onShare,
  status,
}: {
  onShare: () => void;
  status: ResultShareStatus | null;
}) {
  return (
    <div style={resultActionBarStyle}>
      <button
        type="button"
        onClick={onShare}
        aria-label="Share result"
        disabled={status === 'sharing'}
        style={resultShareButtonStyle}
      >
        <ShareIcon />
        <span>{status === 'sharing' ? 'Preparing...' : 'Share result'}</span>
      </button>
      {status && status !== 'sharing' && (
        <div role="status" aria-live="polite" style={resultShareStatusStyle}>
          {shareStatusLabel(status)}
        </div>
      )}
    </div>
  );
}

function shareStatusLabel(status: ShareStatus): string {
  if (status === 'shared') return 'Shared result';
  if (status === 'copied') return 'Copied result';
  if (status === 'cancelled') return 'Share cancelled';
  return 'Share unavailable';
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: 18, height: 18 }}>
      <path
        d="M12 16V4m0 0 4 4m-4-4-4 4"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.2"
      />
      <path
        d="M5 12v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.2"
      />
    </svg>
  );
}

function NinetyHeroDiagram({
  stubHeight,
  markFromEnd,
  takeUp,
}: {
  stubHeight: number;
  markFromEnd: number;
  takeUp: number;
}) {
  const baselineY = 172;
  const horizontalStartX = 28;
  const bendX = 214;
  const verticalTopY = 34;
  // The stub mark is measured down the upright from the open end (top of the stub).
  const markY = clamp(
    verticalTopY + (markFromEnd / Math.max(stubHeight, 0.0001)) * (baselineY - verticalTopY),
    verticalTopY + 10,
    baselineY - 44,
  );

  return (
    <div style={offsetDiagramFrameStyle}>
      <div style={offsetDiagramHeaderStyle}>
        <span>Bend diagram</span>
        <span className="mono">stub-up</span>
      </div>
      <svg
        viewBox="0 0 360 220"
        role="img"
        aria-label="90 degree stub-up conduit diagram"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        <defs>
          <marker
            id="ninety-arrow"
            markerWidth="8"
            markerHeight="8"
            refX="4"
            refY="4"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--color-blue)" />
          </marker>
          <filter id="ninety-soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow
              dx="0"
              dy="10"
              stdDeviation="5"
              floodColor="#020617"
              floodOpacity="0.24"
            />
          </filter>
        </defs>

        <rect x="8" y="10" width="344" height="196" rx="18" fill="var(--color-diagram-bg)" />
        <g opacity="0.24" stroke="var(--color-diagram-ghost)" strokeWidth="1">
          {Array.from({ length: 8 }).map((_, i) => (
            <line key={`h-${i}`} x1="22" y1={34 + i * 22} x2="338" y2={34 + i * 22} />
          ))}
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={`v-${i}`} x1={36 + i * 32} y1="24" x2={36 + i * 32} y2="194" />
          ))}
        </g>

        <path
          d={`M ${horizontalStartX},${baselineY} L ${bendX - 36},${baselineY} Q ${bendX},${baselineY} ${bendX},${
            baselineY - 36
          } L ${bendX},${verticalTopY}`}
          fill="none"
          stroke="var(--color-diagram-pipe-shadow)"
          strokeWidth="18"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.4"
          transform="translate(0 8)"
        />
        <path
          d={`M ${horizontalStartX},${baselineY} L ${bendX - 36},${baselineY} Q ${bendX},${baselineY} ${bendX},${
            baselineY - 36
          } L ${bendX},${verticalTopY}`}
          fill="none"
          stroke="var(--color-diagram-pipe)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#ninety-soft-shadow)"
        />
        <path
          d={`M ${bendX - 58},${baselineY} L ${bendX - 36},${baselineY} Q ${bendX},${baselineY} ${
            bendX
          },${baselineY - 36} L ${bendX},${baselineY - 58}`}
          fill="none"
          stroke="var(--color-orange)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        <line
          x1={bendX - 17}
          y1={markY}
          x2={bendX + 17}
          y2={markY}
          stroke="var(--color-blue)"
          strokeWidth="3"
        />
        <text
          x={bendX - 26}
          y={markY + 4}
          fill="var(--color-blue)"
          textAnchor="end"
          fontSize="12"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          mark {formatInches(markFromEnd)}
        </text>

        <line
          x1="286"
          y1={baselineY}
          x2="286"
          y2={verticalTopY}
          stroke="var(--color-blue)"
          strokeWidth="2"
          markerStart="url(#ninety-arrow)"
          markerEnd="url(#ninety-arrow)"
        />
        <text
          x="340"
          y="106"
          fill="var(--color-blue)"
          fontSize="13"
          fontWeight="900"
          textAnchor="end"
        >
          {formatInches(stubHeight)}
        </text>
        <text
          x="34"
          y="44"
          fill="var(--color-text-muted)"
          fontSize="11"
          fontWeight="750"
          fontFamily="var(--font-mono)"
        >
          {`90\u00b0 stub - take-up ${formatInches(takeUp)}`}
        </text>
      </svg>
    </div>
  );
}

function BackToBackHeroDiagram({
  stub1,
  stub2,
  betweenStubs,
  firstMark,
  secondMark,
}: {
  stub1: number;
  stub2: number;
  betweenStubs: number;
  firstMark: number;
  secondMark: number;
}) {
  const baselineY = 168;
  const leftBendX = 112;
  const rightBendX = 258;
  const topY = 54;
  const stub1TopY = clamp(baselineY - stub1 * 7, 40, 118);
  const stub2TopY = clamp(baselineY - stub2 * 6, 36, 110);
  const mark1X = clamp(46 + firstMark * 6, 44, leftBendX - 18);
  const mark2X = clamp(mark1X + betweenStubs * 4, leftBendX + 52, rightBendX + 38);
  const path = `M 34,${baselineY} L ${leftBendX - 34},${baselineY} Q ${leftBendX},${baselineY} ${leftBendX},${
    baselineY - 34
  } L ${leftBendX},${topY} L ${rightBendX - 34},${topY} Q ${rightBendX},${topY} ${rightBendX},${
    topY + 34
  } L ${rightBendX},${baselineY}`;

  return (
    <div style={offsetDiagramFrameStyle}>
      <div style={offsetDiagramHeaderStyle}>
        <span>Bend diagram</span>
        <span className="mono">back-to-back</span>
      </div>
      <svg
        viewBox="0 0 360 220"
        role="img"
        aria-label="Back-to-back conduit diagram"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        <defs>
          <marker
            id="back-to-back-arrow"
            markerWidth="8"
            markerHeight="8"
            refX="4"
            refY="4"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--color-blue)" />
          </marker>
          <filter id="back-to-back-soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow
              dx="0"
              dy="10"
              stdDeviation="5"
              floodColor="#020617"
              floodOpacity="0.24"
            />
          </filter>
        </defs>

        <rect x="8" y="10" width="344" height="196" rx="18" fill="var(--color-diagram-bg)" />
        <g opacity="0.24" stroke="var(--color-diagram-ghost)" strokeWidth="1">
          {Array.from({ length: 8 }).map((_, i) => (
            <line key={`h-${i}`} x1="22" y1={34 + i * 22} x2="338" y2={34 + i * 22} />
          ))}
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={`v-${i}`} x1={36 + i * 32} y1="24" x2={36 + i * 32} y2="194" />
          ))}
        </g>

        <path
          d={path}
          fill="none"
          stroke="var(--color-diagram-pipe-shadow)"
          strokeWidth="18"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.4"
          transform="translate(0 8)"
        />
        <path
          d={path}
          fill="none"
          stroke="var(--color-diagram-pipe)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#back-to-back-soft-shadow)"
        />
        <path
          d={`M ${leftBendX - 52},${baselineY} L ${leftBendX - 34},${baselineY} Q ${leftBendX},${baselineY} ${leftBendX},${
            baselineY - 34
          } L ${leftBendX},${baselineY - 58}`}
          fill="none"
          stroke="var(--color-orange)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={`M ${rightBendX - 58},${topY} L ${rightBendX - 34},${topY} Q ${rightBendX},${topY} ${rightBendX},${
            topY + 34
          } L ${rightBendX},${topY + 58}`}
          fill="none"
          stroke="var(--color-orange)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        <line
          x1={mark1X}
          y1={baselineY + 6}
          x2={mark1X}
          y2={baselineY + 28}
          stroke="var(--color-blue)"
          strokeWidth="3"
        />
        <line
          x1={mark2X}
          y1={baselineY + 6}
          x2={mark2X}
          y2={baselineY + 28}
          stroke="var(--color-blue)"
          strokeWidth="3"
        />
        <line
          x1={mark1X}
          y1={baselineY + 42}
          x2={mark2X}
          y2={baselineY + 42}
          stroke="var(--color-blue)"
          strokeWidth="2"
          markerStart="url(#back-to-back-arrow)"
          markerEnd="url(#back-to-back-arrow)"
        />
        <text
          x={mark1X}
          y={baselineY + 38}
          fill="var(--color-blue)"
          textAnchor="middle"
          fontSize="11"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          {formatInches(firstMark)}
        </text>
        <text
          x={mark2X}
          y={baselineY + 38}
          fill="var(--color-blue)"
          textAnchor="middle"
          fontSize="11"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          {formatInches(secondMark)}
        </text>
        <text
          x={(mark1X + mark2X) / 2}
          y={baselineY + 60}
          fill="var(--color-text-muted)"
          textAnchor="middle"
          fontSize="11"
          fontWeight="850"
          fontFamily="var(--font-mono)"
        >
          space {formatInches(betweenStubs)}
        </text>

        <line
          x1="318"
          y1={baselineY}
          x2="318"
          y2={stub1TopY}
          stroke="var(--color-blue)"
          strokeWidth="2"
          markerStart="url(#back-to-back-arrow)"
          markerEnd="url(#back-to-back-arrow)"
        />
        <text
          x="340"
          y={(baselineY + stub1TopY) / 2 + 4}
          fill="var(--color-blue)"
          fontSize="12"
          fontWeight="900"
          textAnchor="end"
        >
          stub 1 {formatInches(stub1)}
        </text>
        <text
          x="34"
          y="38"
          fill="var(--color-accent)"
          fontSize="11"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          stub 2 {formatInches(stub2)}
        </text>
        <line
          x1="72"
          y1={baselineY}
          x2="72"
          y2={stub2TopY}
          stroke="var(--color-accent)"
          strokeWidth="2"
          markerStart="url(#back-to-back-arrow)"
          markerEnd="url(#back-to-back-arrow)"
          opacity="0.78"
        />
      </svg>
    </div>
  );
}

function Saddle3HeroDiagram({
  obstructionHeight,
  firstSideMark,
  centerMark,
  secondSideMark,
  centerAngle,
  sideBendAngle,
  shrink,
}: {
  obstructionHeight: number;
  firstSideMark: number;
  centerMark: number;
  secondSideMark: number;
  centerAngle: number;
  sideBendAngle: number;
  shrink: number;
}) {
  const baselineY = 166;
  const firstX = 90;
  const centerX = 180;
  const thirdX = 270;
  const startX = 28;
  const endX = 334;
  const liftPx = clamp(obstructionHeight * 18, 44, 92);
  const peakY = baselineY - liftPx;
  const obstructionTopY = peakY + 18;

  // Real conduit bends on a radius — fillet the three bends instead of a sharp V.
  const legLength = Math.hypot(centerX - firstX, peakY - baselineY);
  const bendRadius = clamp(legLength * 0.32, 10, 22);
  const along = (fromX: number, fromY: number, toX: number, toY: number, dist: number) => {
    const dx = toX - fromX;
    const dy = toY - fromY;
    const len = Math.hypot(dx, dy) || 1;
    return `${(fromX + (dx / len) * dist).toFixed(2)},${(fromY + (dy / len) * dist).toFixed(2)}`;
  };
  const footLApproach = along(firstX, baselineY, startX, baselineY, bendRadius);
  const footLRise = along(firstX, baselineY, centerX, peakY, bendRadius);
  const crownLeft = along(centerX, peakY, firstX, baselineY, bendRadius);
  const crownRight = along(centerX, peakY, thirdX, baselineY, bendRadius);
  const footRRise = along(thirdX, baselineY, centerX, peakY, bendRadius);
  const footRApproach = along(thirdX, baselineY, endX, baselineY, bendRadius);
  const path =
    `M ${startX},${baselineY} L ${footLApproach} Q ${firstX},${baselineY} ${footLRise}` +
    ` L ${crownLeft} Q ${centerX},${peakY} ${crownRight}` +
    ` L ${footRRise} Q ${thirdX},${baselineY} ${footRApproach} L ${endX},${baselineY}`;
  // The center bend (orange) spans the crown plus the upper half of both rise
  // legs; blue stays near the side-bend feet.
  const crownSpread = legLength * 0.45;
  const legSplitL = along(firstX, baselineY, centerX, peakY, crownSpread);
  const legSplitR = along(thirdX, baselineY, centerX, peakY, crownSpread);
  const leftLegPath = `M ${footLApproach} Q ${firstX},${baselineY} ${footLRise} L ${legSplitL}`;
  const crownPath = `M ${legSplitL} L ${crownLeft} Q ${centerX},${peakY} ${crownRight} L ${legSplitR}`;
  const rightLegPath = `M ${legSplitR} L ${footRRise} Q ${thirdX},${baselineY} ${footRApproach}`;
  const obstructionRadius = clamp(Math.min(obstructionHeight * 7, baselineY - peakY - 8), 0, 30);
  const showObstruction = obstructionRadius >= 10;

  return (
    <div style={offsetDiagramFrameStyle}>
      <div style={offsetDiagramHeaderStyle}>
        <span>Bend diagram</span>
        <span className="mono">3-point saddle</span>
      </div>
      <svg
        viewBox="0 0 360 220"
        role="img"
        aria-label="3-point saddle conduit diagram"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        <defs>
          <marker
            id="saddle3-arrow"
            markerWidth="8"
            markerHeight="8"
            refX="4"
            refY="4"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--color-blue)" />
          </marker>
          <filter id="saddle3-soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow
              dx="0"
              dy="10"
              stdDeviation="5"
              floodColor="#020617"
              floodOpacity="0.24"
            />
          </filter>
        </defs>

        <rect x="8" y="10" width="344" height="196" rx="18" fill="var(--color-diagram-bg)" />
        <g opacity="0.24" stroke="var(--color-diagram-ghost)" strokeWidth="1">
          {Array.from({ length: 8 }).map((_, i) => (
            <line key={`h-${i}`} x1="22" y1={34 + i * 22} x2="338" y2={34 + i * 22} />
          ))}
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={`v-${i}`} x1={36 + i * 32} y1="24" x2={36 + i * 32} y2="194" />
          ))}
        </g>

        {showObstruction && (
          <>
            <circle
              cx={centerX}
              cy={baselineY - 2}
              r={obstructionRadius}
              fill="var(--color-blue-soft)"
              stroke="var(--color-blue)"
              strokeWidth="1.5"
            />
            <circle
              cx={centerX}
              cy={baselineY - 2}
              r={Math.max(4, obstructionRadius - 8)}
              fill="none"
              stroke="var(--color-diagram-ghost)"
              strokeWidth="1.4"
            />
          </>
        )}
        <path
          d={path}
          fill="none"
          stroke="var(--color-diagram-pipe-shadow)"
          strokeWidth="18"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.4"
          transform="translate(0 8)"
        />
        <path
          d={path}
          fill="none"
          stroke="var(--color-diagram-pipe)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#saddle3-soft-shadow)"
        />
        <path
          d={leftLegPath}
          fill="none"
          stroke="var(--color-blue)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={crownPath}
          fill="none"
          stroke="var(--color-orange)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={rightLegPath}
          fill="none"
          stroke="var(--color-blue)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {[firstX, centerX, thirdX].map((x, index) => (
          <circle
            key={`bend-${index}`}
            cx={x}
            cy={index === 1 ? peakY : baselineY}
            r="4"
            fill="var(--color-diagram-bg)"
            stroke="var(--color-measure)"
            strokeWidth="2.5"
          />
        ))}

        {[firstX, centerX, thirdX].map((x, index) => (
          <line
            key={`mark-${index}`}
            x1={x}
            y1={baselineY + 8}
            x2={x}
            y2={baselineY + 28}
            stroke={index === 1 ? 'var(--color-orange)' : 'var(--color-blue)'}
            strokeWidth="3"
          />
        ))}
        <text
          x={firstX}
          y={baselineY + 43}
          fill="var(--color-blue)"
          textAnchor="middle"
          fontSize="11"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          {formatInches(firstSideMark)}
        </text>
        <text
          x={centerX}
          y={baselineY + 43}
          fill="var(--color-orange)"
          textAnchor="middle"
          fontSize="11"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          center {formatInches(centerMark)}
        </text>
        <text
          x={thirdX}
          y={baselineY + 43}
          fill="var(--color-blue)"
          textAnchor="middle"
          fontSize="11"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          {formatInches(secondSideMark)}
        </text>

        <line
          x1="306"
          y1={baselineY}
          x2="306"
          y2={obstructionTopY}
          stroke="var(--color-blue)"
          strokeWidth="2"
          markerStart="url(#saddle3-arrow)"
          markerEnd="url(#saddle3-arrow)"
        />
        <text
          x="340"
          y={(baselineY + obstructionTopY) / 2 + 4}
          fill="var(--color-blue)"
          fontSize="13"
          fontWeight="900"
          textAnchor="end"
        >
          {formatInches(obstructionHeight)}
        </text>
        <text
          x="34"
          y="44"
          fill="var(--color-text-muted)"
          fontSize="11"
          fontWeight="750"
          fontFamily="var(--font-mono)"
        >
          {`${formatAngle(sideBendAngle)} - ${formatAngle(centerAngle)} - ${formatAngle(
            sideBendAngle,
          )} - shrink ${formatInches(shrink)}`}
        </text>
      </svg>
    </div>
  );
}

function Saddle4HeroDiagram({
  obstructionHeight,
  obstructionWidth,
  angle,
  firstMark,
  secondMark,
  thirdMark,
  fourthMark,
  offsetSpacing,
}: {
  obstructionHeight: number;
  obstructionWidth: number;
  angle: number;
  firstMark: number;
  secondMark: number;
  thirdMark: number;
  fourthMark: number;
  offsetSpacing: number;
}) {
  const baselineY = 166;
  const topY = baselineY - clamp(obstructionHeight * 16, 48, 92);
  const startX = 26;
  const firstX = 72;
  const secondX = 132;
  const thirdX = 228;
  const fourthX = 288;
  const endX = 334;
  const obstructionTopY = topY + 18;
  const path = `M ${startX},${baselineY} L ${firstX},${baselineY} L ${secondX},${topY} L ${thirdX},${topY} L ${fourthX},${baselineY} L ${endX},${baselineY}`;

  return (
    <div style={offsetDiagramFrameStyle}>
      <div style={offsetDiagramHeaderStyle}>
        <span>Bend diagram</span>
        <span className="mono">4-point saddle</span>
      </div>
      <svg
        viewBox="0 0 360 220"
        role="img"
        aria-label="4-point saddle conduit diagram"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        <defs>
          <marker
            id="saddle4-arrow"
            markerWidth="8"
            markerHeight="8"
            refX="4"
            refY="4"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--color-blue)" />
          </marker>
          <filter id="saddle4-soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow
              dx="0"
              dy="10"
              stdDeviation="5"
              floodColor="#020617"
              floodOpacity="0.24"
            />
          </filter>
        </defs>

        <rect x="8" y="10" width="344" height="196" rx="18" fill="var(--color-diagram-bg)" />
        <g opacity="0.24" stroke="var(--color-diagram-ghost)" strokeWidth="1">
          {Array.from({ length: 8 }).map((_, i) => (
            <line key={`h-${i}`} x1="22" y1={34 + i * 22} x2="338" y2={34 + i * 22} />
          ))}
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={`v-${i}`} x1={36 + i * 32} y1="24" x2={36 + i * 32} y2="194" />
          ))}
        </g>

        <rect
          x={secondX}
          y={obstructionTopY}
          width={thirdX - secondX}
          height={Math.max(20, baselineY - obstructionTopY)}
          rx="5"
          fill="var(--color-blue-soft)"
          stroke="var(--color-blue)"
          strokeWidth="1.5"
          strokeDasharray="5 5"
        />
        <path
          d={path}
          fill="none"
          stroke="var(--color-diagram-pipe-shadow)"
          strokeWidth="18"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.4"
          transform="translate(0 8)"
        />
        <path
          d={path}
          fill="none"
          stroke="var(--color-diagram-pipe)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#saddle4-soft-shadow)"
        />
        <path
          d={`M ${firstX - 20},${baselineY} L ${firstX},${baselineY} L ${secondX},${topY}`}
          fill="none"
          stroke="var(--color-blue)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={`M ${secondX},${topY} L ${thirdX},${topY}`}
          fill="none"
          stroke="var(--color-orange)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={`M ${thirdX},${topY} L ${fourthX},${baselineY} L ${fourthX + 20},${baselineY}`}
          fill="none"
          stroke="var(--color-blue)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {[firstX, secondX, thirdX, fourthX].map((x, index) => (
          <line
            key={`saddle4-mark-${index}`}
            x1={x}
            y1={baselineY + 7}
            x2={x}
            y2={baselineY + 27}
            stroke={index === 1 || index === 2 ? 'var(--color-orange)' : 'var(--color-blue)'}
            strokeWidth="3"
          />
        ))}
        {[
          [firstX, firstMark],
          [secondX, secondMark],
          [thirdX, thirdMark],
          [fourthX, fourthMark],
        ].map(([x, mark]) => (
          <text
            key={`saddle4-mark-label-${x}`}
            x={x}
            y={baselineY + 42}
            fill="var(--color-blue)"
            textAnchor="middle"
            fontSize="10"
            fontWeight="900"
            fontFamily="var(--font-mono)"
          >
            {formatInches(mark)}
          </text>
        ))}

        <line
          x1={firstX}
          y1={baselineY + 56}
          x2={secondX}
          y2={baselineY + 56}
          stroke="var(--color-blue)"
          strokeWidth="2"
          markerStart="url(#saddle4-arrow)"
          markerEnd="url(#saddle4-arrow)"
        />
        <text
          x={(firstX + secondX) / 2}
          y={baselineY + 72}
          fill="var(--color-text-muted)"
          textAnchor="middle"
          fontSize="10"
          fontWeight="850"
          fontFamily="var(--font-mono)"
        >
          {formatInches(offsetSpacing)}
        </text>
        <text
          x="30"
          y="42"
          fill="var(--color-text-muted)"
          fontSize="11"
          fontWeight="850"
          fontFamily="var(--font-mono)"
        >
          {`${formatAngle(angle)} bends - ${formatInches(obstructionWidth)} wide`}
        </text>
        <line
          x1="318"
          y1={baselineY}
          x2="318"
          y2={topY}
          stroke="var(--color-blue)"
          strokeWidth="2"
          markerStart="url(#saddle4-arrow)"
          markerEnd="url(#saddle4-arrow)"
        />
        <text
          x="340"
          y={(baselineY + topY) / 2 + 4}
          fill="var(--color-blue)"
          fontSize="12"
          fontWeight="900"
          textAnchor="end"
        >
          {formatInches(obstructionHeight)}
        </text>
      </svg>
    </div>
  );
}

function OffsetHeroDiagram({
  rise,
  angle,
  distance,
  shrink,
  fromEnd,
}: {
  rise: number;
  angle: number;
  distance: number;
  shrink: number;
  fromEnd: number;
}) {
  const baselineY = 166;
  const bend1X = 76;
  const startX = 26;
  const endX = 334;
  const risePx = clamp(rise * 7.5, 42, 96);
  const topY = baselineY - risePx;
  const rampDx = clamp(risePx / Math.tan((angle * Math.PI) / 180), 70, 166);
  const bend2X = Math.min(272, bend1X + rampDx);
  const path = `M ${startX},${baselineY} L ${bend1X},${baselineY} L ${bend2X},${topY} L ${endX},${topY}`;
  const theta = Math.atan2(topY - baselineY, bend2X - bend1X);
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const zone = 22;
  const bend1Zone = `M ${bend1X - zone},${baselineY} L ${bend1X},${baselineY} L ${bend1X + cos * zone},${
    baselineY + sin * zone
  }`;
  const bend2Zone = `M ${bend2X - cos * zone},${topY - sin * zone} L ${bend2X},${topY} L ${
    bend2X + zone
  },${topY}`;
  const betweenY = baselineY + 32;

  return (
    <div style={offsetDiagramFrameStyle}>
      <div style={offsetDiagramHeaderStyle}>
        <span>Bend diagram</span>
        <span className="mono">offset</span>
      </div>
      <svg
        viewBox="0 0 360 220"
        role="img"
        aria-label="2-point offset conduit diagram"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        <defs>
          <marker
            id="offset-arrow"
            markerWidth="8"
            markerHeight="8"
            refX="4"
            refY="4"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--color-blue)" />
          </marker>
          <filter id="offset-soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow
              dx="0"
              dy="10"
              stdDeviation="5"
              floodColor="#020617"
              floodOpacity="0.24"
            />
          </filter>
        </defs>

        <rect x="8" y="10" width="344" height="196" rx="18" fill="var(--color-diagram-bg)" />
        <g opacity="0.24" stroke="var(--color-diagram-ghost)" strokeWidth="1">
          {Array.from({ length: 8 }).map((_, i) => (
            <line key={`h-${i}`} x1="22" y1={34 + i * 22} x2="338" y2={34 + i * 22} />
          ))}
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={`v-${i}`} x1={36 + i * 32} y1="24" x2={36 + i * 32} y2="194" />
          ))}
        </g>

        <path
          d={`M ${bend1X - 18},${baselineY - 6} C ${bend1X + 28},${baselineY - 70} ${
            bend2X - 38
          },${topY + 72} ${bend2X + 18},${topY + 8}`}
          fill="none"
          stroke="var(--color-blue)"
          strokeWidth="1.8"
          strokeDasharray="7 7"
          opacity="0.55"
        />
        <path
          d={path}
          fill="none"
          stroke="var(--color-diagram-pipe-shadow)"
          strokeWidth="18"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.4"
          transform="translate(0 8)"
        />
        <path
          d={path}
          fill="none"
          stroke="var(--color-diagram-pipe)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#offset-soft-shadow)"
        />
        <path
          d={bend1Zone}
          fill="none"
          stroke="var(--color-blue)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={bend2Zone}
          fill="none"
          stroke="var(--color-orange)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        <line
          x1="306"
          y1={baselineY}
          x2="306"
          y2={topY}
          stroke="var(--color-blue)"
          strokeWidth="2"
          markerStart="url(#offset-arrow)"
          markerEnd="url(#offset-arrow)"
        />
        <text
          x="296"
          y={(baselineY + topY) / 2 + 4}
          fill="var(--color-blue)"
          fontSize="13"
          fontWeight="800"
          textAnchor="end"
        >
          {formatInches(rise)}
        </text>

        <line
          x1={bend1X}
          y1={betweenY}
          x2={bend2X}
          y2={betweenY}
          stroke="var(--color-text-muted)"
          strokeWidth="1.5"
          strokeDasharray="4 4"
        />
        <text
          x={(bend1X + bend2X) / 2}
          y={betweenY + 17}
          fill="var(--color-text-primary)"
          textAnchor="middle"
          fontSize="12"
          fontWeight="800"
          fontFamily="var(--font-mono)"
        >
          {formatInches(distance)}
        </text>

        <text
          x={bend2X - 8}
          y={topY - 15}
          fill="var(--color-text-primary)"
          textAnchor="middle"
          fontSize="13"
          fontWeight="850"
          fontFamily="var(--font-mono)"
        >
          {formatAngle(angle)}
        </text>
        <text
          x="34"
          y="194"
          fill="var(--color-text-muted)"
          fontSize="11"
          fontWeight="750"
          fontFamily="var(--font-mono)"
        >
          from end {formatInches(fromEnd)} · shrink {formatInches(shrink)}
        </text>
      </svg>
    </div>
  );
}

function RollingOffsetHeroDiagram({
  rise,
  roll,
  angle,
  trueOffset,
  rollAngle,
  distance,
}: {
  rise: number;
  roll: number;
  angle: number;
  trueOffset: number;
  rollAngle: number;
  distance: number;
}) {
  const originX = 88;
  const originY = 158;
  const rollPx = clamp(roll * 20, 42, 128);
  const risePx = clamp(rise * 20, 42, 128);
  const targetX = originX + rollPx;
  const targetY = originY - risePx;

  return (
    <div style={offsetDiagramFrameStyle}>
      <div style={offsetDiagramHeaderStyle}>
        <span>Bend diagram</span>
        <span className="mono">rolling offset</span>
      </div>
      <svg
        viewBox="0 0 360 220"
        role="img"
        aria-label="Rolling offset conduit diagram"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        <defs>
          <marker
            id="rolling-offset-arrow"
            markerWidth="8"
            markerHeight="8"
            refX="4"
            refY="4"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--color-blue)" />
          </marker>
          <filter id="rolling-offset-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="8" stdDeviation="4" floodColor="#020617" floodOpacity="0.22" />
          </filter>
        </defs>

        <rect x="8" y="10" width="344" height="196" rx="18" fill="var(--color-diagram-bg)" />
        <g opacity="0.22" stroke="var(--color-diagram-ghost)" strokeWidth="1">
          {Array.from({ length: 7 }).map((_, i) => (
            <line key={`h-${i}`} x1="22" y1={36 + i * 24} x2="338" y2={36 + i * 24} />
          ))}
          {Array.from({ length: 9 }).map((_, i) => (
            <line key={`v-${i}`} x1={40 + i * 36} y1="24" x2={40 + i * 36} y2="194" />
          ))}
        </g>

        <path
          d={`M 28,${originY} L ${originX},${originY} L ${targetX},${targetY} L 332,${targetY}`}
          fill="none"
          stroke="var(--color-diagram-pipe-shadow)"
          strokeWidth="18"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.3"
          transform="translate(0 8)"
        />
        <path
          d={`M 28,${originY} L ${originX},${originY} L ${targetX},${targetY} L 332,${targetY}`}
          fill="none"
          stroke="var(--color-diagram-pipe)"
          strokeWidth="11"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#rolling-offset-shadow)"
        />
        <path
          d={`M ${originX - 22},${originY} L ${originX},${originY} L ${originX + 24},${
            originY - 24
          }`}
          fill="none"
          stroke="var(--color-blue)"
          strokeWidth="11"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={`M ${targetX - 24},${targetY + 24} L ${targetX},${targetY} L ${targetX + 24},${
            targetY
          }`}
          fill="none"
          stroke="var(--color-orange)"
          strokeWidth="11"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        <line
          x1={originX}
          y1={originY}
          x2={targetX}
          y2={originY}
          stroke="var(--color-blue)"
          strokeWidth="2"
          strokeDasharray="5 5"
        />
        <line
          x1={targetX}
          y1={originY}
          x2={targetX}
          y2={targetY}
          stroke="var(--color-blue)"
          strokeWidth="2"
          strokeDasharray="5 5"
        />
        <line
          x1={originX}
          y1={originY + 30}
          x2={targetX}
          y2={originY + 30}
          stroke="var(--color-text-muted)"
          strokeWidth="1.5"
          markerStart="url(#rolling-offset-arrow)"
          markerEnd="url(#rolling-offset-arrow)"
        />
        <text
          x={(originX + targetX) / 2}
          y={originY + 50}
          fill="var(--color-blue)"
          textAnchor="middle"
          fontSize="12"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          roll {formatInches(roll)}
        </text>
        <text
          x={targetX + 10}
          y={(originY + targetY) / 2 + 4}
          fill="var(--color-blue)"
          fontSize="12"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          rise {formatInches(rise)}
        </text>
        <text
          x="34"
          y="42"
          fill="var(--color-text-primary)"
          fontSize="12"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          {`true ${formatInches(trueOffset)} - rotate ${formatAngle(rollAngle)}`}
        </text>
        <text
          x="34"
          y="194"
          fill="var(--color-text-muted)"
          fontSize="11"
          fontWeight="750"
          fontFamily="var(--font-mono)"
        >
          {`${formatAngle(angle)} bends - marks ${formatInches(distance)} apart`}
        </text>
      </svg>
    </div>
  );
}

function KickHeroDiagram({
  stubHeight,
  kickOffset,
  kickAngle,
  ninetyMark,
  kickMarkDistance,
}: {
  stubHeight: number;
  kickOffset: number;
  kickAngle: number;
  ninetyMark: number;
  kickMarkDistance: number;
}) {
  const baseY = 166;
  const startX = 28;
  const ninetyX = 174;
  const topY = 42;
  const kickLift = clamp(kickOffset * 18, 30, 72);
  const kickY = topY + kickLift;
  const kickX = ninetyX + clamp(kickOffset * 18, 34, 78);
  const bendArcStartX = ninetyX - 34;
  const path = `M ${startX},${baseY} L ${bendArcStartX},${baseY} Q ${ninetyX},${baseY} ${ninetyX},${
    baseY - 34
  } L ${ninetyX},${kickY} L ${kickX},${topY} L ${kickX},${topY - 12}`;

  return (
    <div style={offsetDiagramFrameStyle}>
      <div style={offsetDiagramHeaderStyle}>
        <span>Bend diagram</span>
        <span className="mono">kick 90</span>
      </div>
      <svg
        viewBox="0 0 360 220"
        role="img"
        aria-label="Kick conduit diagram"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        <defs>
          <marker id="kick-arrow" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
            <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--color-blue)" />
          </marker>
          <filter id="kick-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="8" stdDeviation="4" floodColor="#020617" floodOpacity="0.22" />
          </filter>
        </defs>

        <rect x="8" y="10" width="344" height="196" rx="18" fill="var(--color-diagram-bg)" />
        <g opacity="0.22" stroke="var(--color-diagram-ghost)" strokeWidth="1">
          {Array.from({ length: 7 }).map((_, i) => (
            <line key={`h-${i}`} x1="22" y1={36 + i * 24} x2="338" y2={36 + i * 24} />
          ))}
          {Array.from({ length: 9 }).map((_, i) => (
            <line key={`v-${i}`} x1={40 + i * 36} y1="24" x2={40 + i * 36} y2="194" />
          ))}
        </g>

        <path
          d={path}
          fill="none"
          stroke="var(--color-diagram-pipe-shadow)"
          strokeWidth="18"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.3"
          transform="translate(0 8)"
        />
        <path
          d={path}
          fill="none"
          stroke="var(--color-diagram-pipe)"
          strokeWidth="11"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#kick-shadow)"
        />
        <path
          d={`M ${bendArcStartX - 26},${baseY} L ${bendArcStartX},${baseY} Q ${ninetyX},${baseY} ${ninetyX},${
            baseY - 34
          } L ${ninetyX},${baseY - 58}`}
          fill="none"
          stroke="var(--color-blue)"
          strokeWidth="11"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={`M ${ninetyX},${kickY + 18} L ${ninetyX},${kickY} L ${kickX},${topY} L ${
            kickX
          },${topY - 4}`}
          fill="none"
          stroke="var(--color-orange)"
          strokeWidth="11"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        <line
          x1={ninetyX}
          y1={baseY + 12}
          x2={ninetyX}
          y2={baseY + 34}
          stroke="var(--color-blue)"
          strokeWidth="3"
        />
        <text
          x={ninetyX}
          y={baseY + 52}
          fill="var(--color-blue)"
          textAnchor="middle"
          fontSize="12"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          90 mark {formatInches(ninetyMark)}
        </text>

        <line
          x1={ninetyX + 12}
          y1={kickY + 10}
          x2={kickX + 12}
          y2={topY + 10}
          stroke="var(--color-orange)"
          strokeWidth="2"
          strokeDasharray="5 5"
        />
        <text
          x={kickX + 72}
          y={topY + 24}
          fill="var(--color-orange)"
          textAnchor="end"
          fontSize="12"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          kick {formatInches(kickMarkDistance)}
        </text>

        <line
          x1="302"
          y1={baseY}
          x2="302"
          y2={topY}
          stroke="var(--color-blue)"
          strokeWidth="2"
          markerStart="url(#kick-arrow)"
          markerEnd="url(#kick-arrow)"
        />
        <text
          x="338"
          y={(baseY + topY) / 2 + 4}
          fill="var(--color-blue)"
          textAnchor="end"
          fontSize="12"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          stub {formatInches(stubHeight)}
        </text>
        <text
          x="34"
          y="42"
          fill="var(--color-text-primary)"
          fontSize="12"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          {`kick ${formatInches(kickOffset)} - ${formatAngle(kickAngle)}`}
        </text>
      </svg>
    </div>
  );
}

function KickSetup({
  ninetyMark,
  kickMarkDistance,
  shrink,
  totalLength,
  gainVerified,
}: {
  ninetyMark: number;
  kickMarkDistance: number;
  shrink: number;
  totalLength: number;
  gainVerified: boolean;
}) {
  return (
    <section aria-labelledby="kick-setup-heading" style={rollingSetupStyle}>
      <h2 id="kick-setup-heading" style={rollingSetupTitleStyle}>
        Kick setup
      </h2>
      <div style={rollingSetupGridStyle}>
        <RollingSetupMetric label="Bend 90" value={formatInches(ninetyMark)} featured />
        <RollingSetupMetric label="Kick mark" value={formatInches(kickMarkDistance)} />
        <RollingSetupMetric label="Shrink" value={formatInches(shrink)} />
        <RollingSetupMetric
          label={gainBasedLengthLabel('Cut length', gainVerified)}
          value={formatInches(totalLength)}
        />
      </div>
      <GainEstimateNote gainVerified={gainVerified} />
    </section>
  );
}

function BackToBackSetup({
  firstMark,
  secondMark,
  betweenStubs,
  takeUp,
  totalLength,
  gainVerified,
}: {
  firstMark: number;
  secondMark: number;
  betweenStubs: number;
  takeUp: number;
  totalLength: number;
  gainVerified: boolean;
}) {
  return (
    <section aria-labelledby="back-to-back-setup-heading" style={rollingSetupStyle}>
      <h2 id="back-to-back-setup-heading" style={rollingSetupTitleStyle}>
        Back-to-back setup
      </h2>
      <div style={rollingSetupGridStyle}>
        <RollingSetupMetric label="First bend" value={formatInches(firstMark)} featured />
        <RollingSetupMetric label="Second bend" value={formatInches(secondMark)} />
        <RollingSetupMetric label="Between 90s" value={formatInches(betweenStubs)} />
        <RollingSetupMetric label="Take-up" value={formatInches(takeUp)} />
        <RollingSetupMetric
          label={gainBasedLengthLabel('Cut length', gainVerified)}
          value={formatInches(totalLength)}
        />
      </div>
      <GainEstimateNote gainVerified={gainVerified} />
    </section>
  );
}

function Saddle4Setup({
  firstMark,
  secondMark,
  thirdMark,
  fourthMark,
  offsetSpacing,
  acrossTop,
  shrink,
  totalLength,
  gainVerified,
}: {
  firstMark: number;
  secondMark: number;
  thirdMark: number;
  fourthMark: number;
  offsetSpacing: number;
  acrossTop: number;
  shrink: number;
  totalLength: number;
  gainVerified: boolean;
}) {
  return (
    <section aria-labelledby="saddle4-setup-heading" style={rollingSetupStyle}>
      <h2 id="saddle4-setup-heading" style={rollingSetupTitleStyle}>
        4-point saddle setup
      </h2>
      <div style={rollingSetupGridStyle}>
        <RollingSetupMetric label="First mark" value={formatInches(firstMark)} featured />
        <RollingSetupMetric label="Second mark" value={formatInches(secondMark)} />
        <RollingSetupMetric label="Third mark" value={formatInches(thirdMark)} />
        <RollingSetupMetric label="Fourth mark" value={formatInches(fourthMark)} />
        <RollingSetupMetric label="Offset spacing" value={formatInches(offsetSpacing)} />
        <RollingSetupMetric label="Across top" value={formatInches(acrossTop)} />
        <RollingSetupMetric label="Shrink" value={formatInches(shrink)} />
        <RollingSetupMetric
          label={gainBasedLengthLabel('Cut length', gainVerified)}
          value={formatInches(totalLength)}
        />
      </div>
      <GainEstimateNote gainVerified={gainVerified} />
    </section>
  );
}

function RollingOffsetSetup({
  rollAngle,
  trueOffset,
  distance,
  shrink,
}: {
  rollAngle: number;
  trueOffset: number;
  distance: number;
  shrink: number;
}) {
  return (
    <section aria-labelledby="rolling-setup-heading" style={rollingSetupStyle}>
      <h2 id="rolling-setup-heading" style={rollingSetupTitleStyle}>
        Rolling setup
      </h2>
      <div style={rollingSetupGridStyle}>
        <RollingSetupMetric label="Rotate conduit" value={formatAngle(rollAngle)} featured />
        <RollingSetupMetric label="True offset" value={formatInches(trueOffset)} />
        <RollingSetupMetric label="Mark spacing" value={formatInches(distance)} />
        <RollingSetupMetric label="Shrink" value={formatInches(shrink)} />
      </div>
    </section>
  );
}

function RollingSetupMetric({
  label,
  value,
  featured = false,
}: {
  label: string;
  value: string;
  featured?: boolean;
}) {
  return (
    <div style={featured ? rollingSetupMetricFeaturedStyle : rollingSetupMetricStyle}>
      <span style={rollingSetupMetricLabelStyle}>{label}</span>
      <span className="mono" style={rollingSetupMetricValueStyle}>
        {value}
      </span>
    </div>
  );
}

interface ParallelRunView {
  run: number;
  markShift: number;
  firstMark: number;
  secondMark: number;
}

interface ConcentricRunView {
  run: number;
  radius: number;
  additionalLength: number;
}

function parallelRunsFromResult(result: Record<string, unknown>): ParallelRunView[] {
  const rawRuns = Array.isArray(result.runs) ? result.runs : [];
  return rawRuns.flatMap((run) => {
    if (!run || typeof run !== 'object') return [];
    const record = run as Record<string, unknown>;
    const runNumber = Number(record.run);
    const markShift = Number(record.markShift);
    const firstMark = Number(record.firstMark);
    const secondMark = Number(record.secondMark);
    if (
      !Number.isFinite(runNumber) ||
      !Number.isFinite(markShift) ||
      !Number.isFinite(firstMark) ||
      !Number.isFinite(secondMark)
    ) {
      return [];
    }
    return [{ run: runNumber, markShift, firstMark, secondMark }];
  });
}

function concentricRunsFromResult(result: Record<string, unknown>): ConcentricRunView[] {
  const rawParallels = Array.isArray(result.parallels) ? result.parallels : [];
  return rawParallels.flatMap((parallel, index) => {
    if (!parallel || typeof parallel !== 'object') return [];
    const record = parallel as Record<string, unknown>;
    const radius = Number(record.radius);
    const additionalLength = Number(record.additionalLength);
    if (!Number.isFinite(radius) || !Number.isFinite(additionalLength)) return [];
    return [{ run: index + 1, radius, additionalLength }];
  });
}

function ParallelOffsetHeroDiagram({
  rise,
  angle,
  progression,
  distance,
  runs,
}: {
  rise: number;
  angle: number;
  progression: number;
  distance: number;
  runs: ParallelRunView[];
}) {
  const visibleRuns = runs.slice(0, 4);
  const baselineStartY = 156;
  const bend1X = 78;
  const startX = 24;
  const endX = 336;
  const risePx = clamp(rise * 7, 42, 86);
  const topDelta = risePx;
  const rampDx = clamp(risePx / Math.tan((angle * Math.PI) / 180), 76, 150);
  const bend2X = Math.min(276, bend1X + rampDx);
  const rowGap = visibleRuns.length > 3 ? 28 : 34;

  return (
    <div style={offsetDiagramFrameStyle}>
      <div style={offsetDiagramHeaderStyle}>
        <span>Bend diagram</span>
        <span className="mono">parallel offset</span>
      </div>
      <svg
        viewBox="0 0 360 220"
        role="img"
        aria-label="Parallel offset conduit diagram"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        <defs>
          <marker
            id="parallel-offset-arrow"
            markerWidth="8"
            markerHeight="8"
            refX="4"
            refY="4"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--color-blue)" />
          </marker>
          <filter id="parallel-offset-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="8" stdDeviation="4" floodColor="#020617" floodOpacity="0.22" />
          </filter>
        </defs>

        <rect x="8" y="10" width="344" height="196" rx="18" fill="var(--color-diagram-bg)" />
        <g opacity="0.22" stroke="var(--color-diagram-ghost)" strokeWidth="1">
          {Array.from({ length: 7 }).map((_, i) => (
            <line key={`h-${i}`} x1="22" y1={36 + i * 24} x2="338" y2={36 + i * 24} />
          ))}
          {Array.from({ length: 9 }).map((_, i) => (
            <line key={`v-${i}`} x1={40 + i * 36} y1="24" x2={40 + i * 36} y2="194" />
          ))}
        </g>

        {visibleRuns.map((run, index) => {
          const y = baselineStartY - index * rowGap;
          const topY = y - topDelta;
          const shiftPx = clamp(run.markShift * 7, 0, 54);
          const firstX = bend1X + shiftPx;
          const secondX = bend2X + shiftPx;
          const path = `M ${startX},${y} L ${firstX},${y} L ${secondX},${topY} L ${endX},${topY}`;
          const color = index === 0 ? 'var(--color-orange)' : 'var(--color-blue)';

          return (
            <g key={run.run}>
              <path
                d={path}
                fill="none"
                stroke="var(--color-diagram-pipe-shadow)"
                strokeWidth="16"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.25"
                transform="translate(0 6)"
              />
              <path
                d={path}
                fill="none"
                stroke="var(--color-diagram-pipe)"
                strokeWidth="9"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter={index === 0 ? 'url(#parallel-offset-shadow)' : undefined}
                opacity={index === 0 ? 1 : 0.74}
              />
              <circle cx={firstX} cy={y} r="5" fill={color} />
              <circle cx={secondX} cy={topY} r="5" fill={color} />
              <text
                x={startX + 2}
                y={y - 12}
                fill="var(--color-text-muted)"
                fontSize="10"
                fontWeight="850"
                fontFamily="var(--font-mono)"
              >
                {`run ${run.run}`}
              </text>
            </g>
          );
        })}

        <line
          x1="304"
          y1={baselineStartY}
          x2="304"
          y2={baselineStartY - topDelta}
          stroke="var(--color-blue)"
          strokeWidth="2"
          markerStart="url(#parallel-offset-arrow)"
          markerEnd="url(#parallel-offset-arrow)"
        />
        <text
          x="338"
          y={baselineStartY - topDelta / 2 + 4}
          fill="var(--color-blue)"
          textAnchor="end"
          fontSize="12"
          fontWeight="900"
        >
          {formatInches(rise)}
        </text>
        <text
          x="34"
          y="194"
          fill="var(--color-text-muted)"
          fontSize="11"
          fontWeight="750"
          fontFamily="var(--font-mono)"
        >
          {`${formatAngle(angle)} - between ${formatInches(distance)} - add ${formatInches(
            progression,
          )}`}
        </text>
      </svg>
    </div>
  );
}

function ConcentricHeroDiagram({
  innerRadius,
  spacing,
  angle,
  runs,
}: {
  innerRadius: number;
  spacing: number;
  angle: number;
  runs: ConcentricRunView[];
}) {
  const visibleRuns = runs.slice(0, 5);
  const centerX = 84;
  const centerY = 174;
  const baseRadiusPx = 42;
  const radiusStepPx = visibleRuns.length > 4 ? 22 : 26;
  const sweepAngle = clamp(angle, 20, 180);
  const sweepRad = (sweepAngle * Math.PI) / 180;
  const outerRun = visibleRuns[visibleRuns.length - 1];

  return (
    <div style={offsetDiagramFrameStyle}>
      <div style={offsetDiagramHeaderStyle}>
        <span>Bend diagram</span>
        <span className="mono">concentric</span>
      </div>
      <svg
        viewBox="0 0 360 220"
        role="img"
        aria-label="Concentric conduit run diagram"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        <defs>
          <marker
            id="concentric-arrow"
            markerWidth="8"
            markerHeight="8"
            refX="4"
            refY="4"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--color-blue)" />
          </marker>
          <filter id="concentric-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="8" stdDeviation="4" floodColor="#020617" floodOpacity="0.22" />
          </filter>
        </defs>

        <rect x="8" y="10" width="344" height="196" rx="18" fill="var(--color-diagram-bg)" />
        <g opacity="0.2" stroke="var(--color-diagram-ghost)" strokeWidth="1">
          {Array.from({ length: 7 }).map((_, i) => (
            <line key={`h-${i}`} x1="22" y1={34 + i * 24} x2="338" y2={34 + i * 24} />
          ))}
          {Array.from({ length: 9 }).map((_, i) => (
            <line key={`v-${i}`} x1={40 + i * 36} y1="24" x2={40 + i * 36} y2="194" />
          ))}
        </g>

        {visibleRuns.map((run, index) => {
          const radiusPx = baseRadiusPx + index * radiusStepPx;
          const startX = centerX + radiusPx;
          const startY = centerY;
          const endX = centerX + Math.cos(sweepRad) * radiusPx;
          const endY = centerY - Math.sin(sweepRad) * radiusPx;
          const color = index === 0 ? 'var(--color-orange)' : 'var(--color-blue)';
          const opacity = index === 0 ? 1 : 0.72;
          const path = `M ${startX},${startY} A ${radiusPx},${radiusPx} 0 ${
            sweepAngle > 180 ? 1 : 0
          } 0 ${endX},${endY}`;

          return (
            <g key={run.run}>
              <path
                d={path}
                fill="none"
                stroke="var(--color-diagram-pipe-shadow)"
                strokeWidth="17"
                strokeLinecap="round"
                opacity="0.23"
                transform="translate(0 6)"
              />
              <path
                d={path}
                fill="none"
                stroke="var(--color-diagram-pipe)"
                strokeWidth="10"
                strokeLinecap="round"
                filter={index === 0 ? 'url(#concentric-shadow)' : undefined}
                opacity={opacity}
              />
              <circle cx={startX} cy={startY} r="5" fill={color} />
              <circle cx={endX} cy={endY} r="5" fill={color} />
              <text
                x={Math.min(316, startX + 8)}
                y={startY + 4}
                fill="var(--color-text-muted)"
                fontSize="10"
                fontWeight="850"
                fontFamily="var(--font-mono)"
              >
                {`run ${run.run}`}
              </text>
            </g>
          );
        })}

        <line
          x1={centerX}
          y1={centerY}
          x2={centerX + baseRadiusPx}
          y2={centerY}
          stroke="var(--color-orange)"
          strokeWidth="2"
          markerEnd="url(#concentric-arrow)"
        />
        <text
          x={centerX + baseRadiusPx / 2}
          y={centerY - 10}
          fill="var(--color-orange)"
          textAnchor="middle"
          fontSize="11"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          R {formatInches(innerRadius)}
        </text>
        <text
          x="336"
          y="44"
          fill="var(--color-blue)"
          textAnchor="end"
          fontSize="13"
          fontWeight="900"
        >
          {outerRun ? `outer +${formatInches(outerRun.additionalLength)}` : 'outer run'}
        </text>
        <text
          x="34"
          y="194"
          fill="var(--color-text-muted)"
          fontSize="11"
          fontWeight="750"
          fontFamily="var(--font-mono)"
        >
          {`${formatAngle(angle)} bends - ${formatInches(spacing)} spacing`}
        </text>
      </svg>
    </div>
  );
}

function SegmentedHeroDiagram({
  totalRadius,
  totalAngle,
  segmentAngle,
  segmentCount,
  markSpacing,
}: {
  totalRadius: number;
  totalAngle: number;
  segmentAngle: number;
  segmentCount: number;
  markSpacing: number;
}) {
  const centerX = 180;
  const centerY = 190;
  const radiusPx = 122;
  const safeCount = clamp(Math.round(segmentCount), 1, 80);
  const sweepAngle = clamp(totalAngle, 10, 180);
  const sweepRad = (sweepAngle * Math.PI) / 180;
  const markEvery = Math.max(1, Math.ceil(safeCount / 18));
  const points = Array.from({ length: safeCount + 1 }, (_, index) => {
    const theta = Math.PI - (index / safeCount) * sweepRad;
    return {
      x: centerX + Math.cos(theta) * radiusPx,
      y: centerY - Math.sin(theta) * radiusPx,
      index,
    };
  });
  const path = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x},${point.y}`)
    .join(' ');

  return (
    <div style={offsetDiagramFrameStyle}>
      <div style={offsetDiagramHeaderStyle}>
        <span>Bend diagram</span>
        <span className="mono">segmented sweep</span>
      </div>
      <svg
        viewBox="0 0 360 220"
        role="img"
        aria-label="Segmented sweep conduit diagram"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        <defs>
          <marker
            id="segmented-arrow"
            markerWidth="8"
            markerHeight="8"
            refX="4"
            refY="4"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--color-blue)" />
          </marker>
          <filter id="segmented-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="8" stdDeviation="4" floodColor="#020617" floodOpacity="0.22" />
          </filter>
        </defs>

        <rect x="8" y="10" width="344" height="196" rx="18" fill="var(--color-diagram-bg)" />
        <g opacity="0.2" stroke="var(--color-diagram-ghost)" strokeWidth="1">
          {Array.from({ length: 7 }).map((_, i) => (
            <line key={`h-${i}`} x1="22" y1={34 + i * 24} x2="338" y2={34 + i * 24} />
          ))}
          {Array.from({ length: 9 }).map((_, i) => (
            <line key={`v-${i}`} x1={40 + i * 36} y1="24" x2={40 + i * 36} y2="194" />
          ))}
        </g>

        <path
          d={path}
          fill="none"
          stroke="var(--color-diagram-pipe-shadow)"
          strokeWidth="18"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.26"
          transform="translate(0 7)"
        />
        <path
          d={path}
          fill="none"
          stroke="var(--color-diagram-pipe)"
          strokeWidth="10"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#segmented-shadow)"
        />
        {points
          .filter(
            (point) =>
              point.index === 0 || point.index === safeCount || point.index % markEvery === 0,
          )
          .map((point) => (
            <circle
              key={point.index}
              cx={point.x}
              cy={point.y}
              r={point.index === 0 || point.index === safeCount ? 5 : 3}
              fill={
                point.index === 0 || point.index === safeCount
                  ? 'var(--color-orange)'
                  : 'var(--color-blue)'
              }
              opacity={point.index === 0 || point.index === safeCount ? 1 : 0.76}
            />
          ))}

        <line
          x1={centerX}
          y1={centerY}
          x2={points[0]?.x ?? centerX}
          y2={points[0]?.y ?? centerY}
          stroke="var(--color-blue)"
          strokeWidth="2"
          markerEnd="url(#segmented-arrow)"
        />
        <text
          x="336"
          y="42"
          fill="var(--color-blue)"
          textAnchor="end"
          fontSize="13"
          fontWeight="900"
        >
          {`${segmentCount} bends`}
        </text>
        <text
          x="34"
          y="194"
          fill="var(--color-text-muted)"
          fontSize="11"
          fontWeight="750"
          fontFamily="var(--font-mono)"
        >
          {`${formatAngle(segmentAngle)} each - marks ${formatInches(markSpacing)} apart`}
        </text>
        <text
          x="92"
          y="158"
          fill="var(--color-blue)"
          fontSize="11"
          fontWeight="900"
          fontFamily="var(--font-mono)"
        >
          R {formatInches(totalRadius)}
        </text>
      </svg>
    </div>
  );
}

function GainEstimateNote({ gainVerified }: { gainVerified: boolean }) {
  if (gainVerified) return null;
  return (
    <p style={gainEstimateNoteStyle}>
      Gain is field-reference data for this shoe. Verify cut length against the shoe or manual.
    </p>
  );
}

function ParallelRunMarks({
  runs,
  progression,
  distance,
  shrink,
}: {
  runs: ParallelRunView[];
  progression: number;
  distance: number;
  shrink: number;
}) {
  if (runs.length === 0) return null;

  return (
    <section aria-labelledby="parallel-run-marks-heading" style={parallelRunMarksStyle}>
      <div style={parallelRunMarksHeaderStyle}>
        <div>
          <h2 id="parallel-run-marks-heading" style={parallelRunMarksTitleStyle}>
            Run marks
          </h2>
          <div style={parallelRunMarksSubStyle}>{`Add ${formatInches(progression)} each run`}</div>
        </div>
        <div style={parallelRunMetaStyle}>{`Bends ${formatInches(distance)} apart`}</div>
      </div>
      <div style={parallelRunListStyle}>
        {runs.map((run) => (
          <div key={run.run} style={parallelRunRowStyle}>
            <div style={parallelRunNameStyle}>{`Run ${run.run}`}</div>
            <div className="mono" style={parallelRunMarksValueStyle}>
              {formatInches(run.firstMark)}
            </div>
            <div className="mono" style={parallelRunMarksValueStyle}>
              {formatInches(run.secondMark)}
            </div>
          </div>
        ))}
      </div>
      <div style={parallelRunFootStyle}>{`Shrink ${formatInches(shrink)} on every run.`}</div>
    </section>
  );
}

function ConcentricRunAddOns({
  runs,
  spacing,
  angle,
}: {
  runs: ConcentricRunView[];
  spacing: number;
  angle: number;
}) {
  if (runs.length === 0) return null;

  return (
    <section aria-labelledby="concentric-run-add-ons-heading" style={parallelRunMarksStyle}>
      <div style={parallelRunMarksHeaderStyle}>
        <div>
          <h2 id="concentric-run-add-ons-heading" style={parallelRunMarksTitleStyle}>
            Concentric run add-ons
          </h2>
          <div style={parallelRunMarksSubStyle}>Add length to each outer run</div>
        </div>
        <div style={parallelRunMetaStyle}>{`${formatAngle(angle)} bend`}</div>
      </div>
      <div style={parallelRunListStyle}>
        {runs.map((run) => (
          <div key={run.run} style={parallelRunRowStyle}>
            <div style={parallelRunNameStyle}>{`Run ${run.run}`}</div>
            <div className="mono" style={parallelRunMarksValueStyle}>
              {run.additionalLength === 0 ? 'Base run' : `+${formatInches(run.additionalLength)}`}
            </div>
            <div className="mono" style={parallelRunMarksValueStyle}>
              R {formatInches(run.radius)}
            </div>
          </div>
        ))}
      </div>
      <div style={parallelRunFootStyle}>{`Spacing ${formatInches(spacing)} between runs.`}</div>
    </section>
  );
}

function SegmentedSetup({
  segmentCount,
  markSpacing,
  segmentAngle,
  totalLength,
  totalRadius,
}: {
  segmentCount: number;
  markSpacing: number;
  segmentAngle: number;
  totalLength: number;
  totalRadius: number;
}) {
  return (
    <section aria-label="Segment bend layout" style={rollingSetupStyle}>
      <h2 style={rollingSetupTitleStyle}>Segment bend layout</h2>
      <div style={rollingSetupGridStyle}>
        <RollingSetupMetric label="Bend count" value={String(segmentCount)} featured />
        <RollingSetupMetric label="Mark spacing" value={formatInches(markSpacing)} />
        <RollingSetupMetric label="Each bend" value={formatAngle(segmentAngle)} />
        <RollingSetupMetric label="Target radius" value={formatInches(totalRadius)} />
        <RollingSetupMetric label="Developed length" value={formatInches(totalLength)} />
      </div>
    </section>
  );
}

function OffsetResultCard({
  label,
  value,
  featured = false,
}: {
  label: string;
  value: string;
  featured?: boolean;
}) {
  return (
    <div
      style={{
        background: featured ? 'var(--color-blue)' : 'var(--color-surface)',
        border: `1px solid ${featured ? 'var(--color-blue)' : 'var(--color-border)'}`,
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
        minHeight: 74,
        display: 'grid',
        alignContent: 'center',
        gap: 'var(--space-1)',
        boxShadow: featured ? '0 14px 30px rgba(0, 122, 255, 0.24)' : 'var(--shadow-card)',
      }}
    >
      <span
        style={{
          color: featured ? 'white' : 'var(--color-text-muted)',
          opacity: featured ? 0.84 : 1,
          fontSize: '0.68rem',
          fontWeight: 850,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <span
        className="mono"
        style={{
          color: featured ? 'white' : 'var(--color-text-primary)',
          fontSize: featured ? 'clamp(1.8rem, 8vw, 2.35rem)' : 'clamp(1.25rem, 6vw, 1.7rem)',
          fontWeight: 900,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function InputField({ spec, value, angleIncrement, onChange }: InputFieldProps) {
  const { lengthUnit, roundToNearest } = useAppSettings();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(() => formatEditableMeasurementValue(value, spec, lengthUnit));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) setDraft(formatEditableMeasurementValue(value, spec, lengthUnit));
  }, [isEditing, lengthUnit, spec, value]);

  useEffect(() => {
    if (!isEditing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditing]);

  if (spec.type.kind === 'angle') {
    const presets = spec.type.presets ?? [10, 22.5, 30, 45, 60];
    const minAngle = spec.type.min ?? 5;
    const maxAngle = spec.type.max ?? 89;
    const presetGridColumns =
      presets.length > 5
        ? 'repeat(auto-fit, minmax(72px, 1fr))'
        : `repeat(${presets.length}, minmax(0, 1fr))`;
    const nudge = (direction: -1 | 1) => {
      void lightHaptic();
      onChange(clampAngle(roundAngle(value + direction * angleIncrement), minAngle, maxAngle));
    };
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
          gridColumn: '1 / -1',
          marginTop: 'var(--space-1)',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '56px 1fr 56px',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
        >
          <button
            type="button"
            onClick={() => nudge(-1)}
            aria-label={`Decrease angle by ${formatAngleIncrement(angleIncrement)}`}
            style={angleNudgeButtonStyle}
          >
            -{formatAngleIncrement(angleIncrement)}
          </button>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
              {spec.label}
            </div>
            <div
              className="mono"
              style={{
                color: 'var(--color-accent)',
                fontSize: 'var(--text-4xl)',
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              {formatAngle(value)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => nudge(1)}
            aria-label={`Increase angle by ${formatAngleIncrement(angleIncrement)}`}
            style={angleNudgeButtonStyle}
          >
            +{formatAngleIncrement(angleIncrement)}
          </button>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: presetGridColumns,
            gap: 'var(--space-2)',
          }}
        >
          {presets.map((a) => {
            const active = Math.abs(value - a) < 0.01;
            return (
              <button
                key={a}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  void lightHaptic();
                  onChange(a);
                }}
                style={{
                  padding: 'var(--space-2) var(--space-1)',
                  minWidth: 0,
                  background: active ? 'var(--color-accent)' : 'var(--color-bg)',
                  color: active ? 'var(--color-text-on-orange)' : 'var(--color-text-primary)',
                  border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 800,
                  fontSize: 'var(--text-sm)',
                }}
              >
                {formatAngle(a)}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const t = spec.type;
  const displayLabel = shortInputLabel(spec);
  const label = displayLabel.toUpperCase();
  const nudgeStep = t.unit === 'in' ? fractionPrecisionToInches(roundToNearest) : (t.step ?? 0.25);
  const nudgeStepLabel = formatMeasurementValue(nudgeStep, t.unit, lengthUnit);
  const canDecrease = t.min === undefined || value > t.min;
  const canIncrease = t.max === undefined || value < t.max;
  const nudgeValue = (direction: -1 | 1) => {
    void lightHaptic();
    onChange(clampNumber(value + direction * nudgeStep, t.min, t.max));
  };
  const commitDraft = () => {
    const parsed = parseEditableMeasurementValue(draft, spec, lengthUnit);
    if (parsed === null) {
      setDraft(formatEditableMeasurementValue(value, spec, lengthUnit));
      setIsEditing(false);
      return;
    }
    onChange(clampNumber(parsed, t.min, t.max));
    void lightHaptic();
    setIsEditing(false);
  };
  const setEditedValue = (next: number) => {
    const clamped = clampNumber(next, t.min, t.max);
    onChange(clamped);
    setDraft(formatEditableMeasurementValue(clamped, spec, lengthUnit));
    void lightHaptic();
  };

  if (isEditing && t.unit !== 'in') {
    return (
      <label style={measurementCardStyle}>
        <span style={measurementLabelStyle}>
          {label}
          {measurementUnitSuffix(t.unit, lengthUnit, true)}
        </span>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitDraft();
            }
            if (event.key === 'Escape') {
              setDraft(formatEditableMeasurementValue(value, spec, lengthUnit));
              setIsEditing(false);
            }
          }}
          aria-label={label}
          inputMode={t.unit === 'in' && lengthUnit !== 'Centimeters' ? 'text' : 'decimal'}
          className="mono"
          style={measurementInputStyle}
        />
      </label>
    );
  }

  return (
    <>
      <div style={measurementStepperCardStyle}>
        <button
          type="button"
          onClick={() => {
            void lightHaptic();
            setIsEditing(true);
          }}
          aria-label={`Edit ${spec.label}`}
          style={measurementValueButtonStyle}
        >
          <span style={measurementLabelStyle}>
            {label}
            {measurementUnitSuffix(t.unit, lengthUnit, false)}
          </span>
          <span
            className="mono"
            style={{
              color: 'var(--color-text-primary)',
              fontSize: 'clamp(1.55rem, 7vw, 2.15rem)',
              fontWeight: 850,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {formatMeasurementValue(value, t.unit, lengthUnit)}
          </span>
        </button>
        <span style={measurementStepperRailStyle}>
          <button
            type="button"
            onClick={() => nudgeValue(-1)}
            disabled={!canDecrease}
            aria-label={`Decrease ${spec.label} by ${nudgeStepLabel}`}
            style={
              canDecrease ? measurementStepperButtonStyle : measurementStepperButtonDisabledStyle
            }
          >
            -
          </button>
          <button
            type="button"
            onClick={() => nudgeValue(1)}
            disabled={!canIncrease}
            aria-label={`Increase ${spec.label} by ${nudgeStepLabel}`}
            style={
              canIncrease ? measurementStepperButtonStyle : measurementStepperButtonDisabledStyle
            }
          >
            +
          </button>
        </span>
      </div>

      {isEditing && t.unit === 'in' && (
        <div style={lengthEditorBackdropStyle}>
          <section
            role="dialog"
            aria-modal="true"
            aria-label={`Edit ${displayLabel}`}
            style={lengthEditorSheetStyle}
          >
            <div style={lengthEditorHeaderStyle}>
              <div>
                <div style={lengthEditorEyebrowStyle}>Edit length</div>
                <h2 style={lengthEditorTitleStyle}>{displayLabel}</h2>
              </div>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                aria-label={`Close ${displayLabel} editor`}
                style={lengthEditorCloseButtonStyle}
              >
                x
              </button>
            </div>

            <div className="mono" style={lengthEditorValueStyle}>
              {formatMeasurementValue(value, t.unit, lengthUnit)}
            </div>

            <LengthEditorStepper
              label="Whole inches"
              value={`${Math.floor(Math.max(0, value))} in`}
              decreaseLabel={`Decrease ${displayLabel} inches`}
              increaseLabel={`Increase ${displayLabel} inches`}
              canDecrease={canDecrease}
              canIncrease={canIncrease}
              onDecrease={() => setEditedValue(value - 1)}
              onIncrease={() => setEditedValue(value + 1)}
            />

            <LengthEditorStepper
              label="Fraction"
              value={formatFractionValue(value)}
              decreaseLabel={`Decrease ${displayLabel} fraction by ${nudgeStepLabel}`}
              increaseLabel={`Increase ${displayLabel} fraction by ${nudgeStepLabel}`}
              canDecrease={canDecrease}
              canIncrease={canIncrease}
              onDecrease={() => setEditedValue(value - nudgeStep)}
              onIncrease={() => setEditedValue(value + nudgeStep)}
            />

            <label style={lengthEditorManualStyle}>
              <span style={measurementLabelStyle}>Type exact value</span>
              <input
                type="text"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onFocus={(event) => event.currentTarget.select()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitDraft();
                  }
                  if (event.key === 'Escape') {
                    setDraft(formatEditableMeasurementValue(value, spec, lengthUnit));
                    setIsEditing(false);
                  }
                }}
                aria-label={`${displayLabel} exact value`}
                inputMode={lengthUnit !== 'Centimeters' ? 'text' : 'decimal'}
                className="mono"
                style={lengthEditorInputStyle}
              />
            </label>

            <button type="button" onClick={commitDraft} style={lengthEditorDoneButtonStyle}>
              Done
            </button>
          </section>
        </div>
      )}
    </>
  );
}

function LengthEditorStepper({
  label,
  value,
  decreaseLabel,
  increaseLabel,
  canDecrease,
  canIncrease,
  onDecrease,
  onIncrease,
}: {
  label: string;
  value: string;
  decreaseLabel: string;
  increaseLabel: string;
  canDecrease: boolean;
  canIncrease: boolean;
  onDecrease: () => void;
  onIncrease: () => void;
}) {
  return (
    <div style={lengthEditorControlStyle}>
      <button
        type="button"
        onClick={onDecrease}
        disabled={!canDecrease}
        aria-label={decreaseLabel}
        style={canDecrease ? lengthEditorStepButtonStyle : lengthEditorStepButtonDisabledStyle}
      >
        -
      </button>
      <div style={lengthEditorControlValueStyle}>
        <span style={lengthEditorControlLabelStyle}>{label}</span>
        <span className="mono" style={lengthEditorControlNumberStyle}>
          {value}
        </span>
      </div>
      <button
        type="button"
        onClick={onIncrease}
        disabled={!canIncrease}
        aria-label={increaseLabel}
        style={canIncrease ? lengthEditorStepButtonStyle : lengthEditorStepButtonDisabledStyle}
      >
        +
      </button>
    </div>
  );
}

function ResetIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      style={{ width: 19, height: 19, display: 'block' }}
    >
      <path
        d="M4.5 6.8v5h5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.2"
      />
      <path
        d="M5.2 11.4a7 7 0 1 0 2-5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.2"
      />
    </svg>
  );
}

const backButtonStyle: CSSProperties = {
  width: 34,
  height: 34,
  minWidth: 34,
  minHeight: 34,
  display: 'grid',
  placeItems: 'center',
  color: 'var(--color-text-primary)',
  borderRadius: 'var(--radius-pill)',
  marginLeft: '-8px',
};

const resetButtonStyle: CSSProperties = {
  width: 34,
  height: 34,
  minWidth: 34,
  minHeight: 34,
  display: 'grid',
  placeItems: 'center',
  color: 'var(--color-text-secondary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-pill)',
};

const measurementCardStyle: CSSProperties = {
  minHeight: 70,
  width: '100%',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-3)',
  display: 'grid',
  alignContent: 'center',
  gap: 'var(--space-1)',
  textAlign: 'left',
  boxShadow: 'var(--shadow-card)',
};

const measurementStepperCardStyle: CSSProperties = {
  ...measurementCardStyle,
  gridTemplateColumns: 'minmax(0, 1fr) 38px',
  alignItems: 'stretch',
  alignContent: 'stretch',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-2) var(--space-2) var(--space-3)',
};

const measurementValueButtonStyle: CSSProperties = {
  minWidth: 0,
  background: 'transparent',
  border: 0,
  color: 'inherit',
  padding: 0,
  display: 'grid',
  alignContent: 'center',
  justifyItems: 'start',
  gap: 'var(--space-1)',
  textAlign: 'left',
};

const measurementStepperRailStyle: CSSProperties = {
  display: 'grid',
  gridTemplateRows: '1fr 1fr',
  gap: '4px',
};

const measurementStepperButtonStyle: CSSProperties = {
  width: 38,
  minWidth: 38,
  minHeight: 27,
  display: 'grid',
  placeItems: 'center',
  color: 'var(--color-blue)',
  background: 'var(--color-blue-soft)',
  border: '1px solid rgba(0, 122, 255, 0.28)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-lg)',
  fontWeight: 900,
  lineHeight: 1,
};

const measurementStepperButtonDisabledStyle: CSSProperties = {
  ...measurementStepperButtonStyle,
  color: 'var(--color-text-muted)',
  background: 'var(--color-bg)',
  borderColor: 'var(--color-border)',
  opacity: 0.42,
};

const measurementLabelStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: '0.68rem',
  fontWeight: 850,
  letterSpacing: '0.08em',
};

const measurementInputStyle: CSSProperties = {
  width: '100%',
  minWidth: 0,
  minHeight: 34,
  background: 'transparent',
  color: 'var(--color-text-primary)',
  border: 0,
  outline: 0,
  padding: 0,
  fontSize: 'clamp(1.55rem, 7vw, 2.15rem)',
  fontWeight: 850,
  lineHeight: 1,
};

const lengthEditorBackdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 50,
  display: 'grid',
  alignItems: 'end',
  padding: 'var(--space-3)',
  background: 'rgba(2, 6, 23, 0.58)',
};

const lengthEditorSheetStyle: CSSProperties = {
  width: 'min(100%, 430px)',
  maxHeight: 'min(86vh, 620px)',
  margin: '0 auto',
  overflowY: 'auto',
  display: 'grid',
  gap: 'var(--space-3)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: '22px 22px var(--radius-lg) var(--radius-lg)',
  padding: 'var(--space-4)',
  boxShadow: '0 24px 60px rgba(2, 6, 23, 0.34)',
};

const lengthEditorHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
};

const lengthEditorEyebrowStyle: CSSProperties = {
  color: 'var(--color-accent)',
  fontSize: '0.68rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const lengthEditorTitleStyle: CSSProperties = {
  margin: '3px 0 0',
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-2xl)',
  fontWeight: 900,
  lineHeight: 1,
};

const lengthEditorCloseButtonStyle: CSSProperties = {
  width: 38,
  minWidth: 38,
  height: 38,
  display: 'grid',
  placeItems: 'center',
  color: 'var(--color-text-secondary)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-pill)',
  fontSize: 'var(--text-lg)',
  fontWeight: 900,
};

const lengthEditorValueStyle: CSSProperties = {
  minHeight: 70,
  display: 'grid',
  placeItems: 'center',
  color: 'var(--color-text-primary)',
  background: 'var(--color-blue-soft)',
  border: '1px solid rgba(0, 122, 255, 0.28)',
  borderRadius: 'var(--radius-lg)',
  fontSize: 'clamp(2.25rem, 12vw, 3.25rem)',
  fontWeight: 900,
  lineHeight: 1,
  whiteSpace: 'nowrap',
};

const lengthEditorControlStyle: CSSProperties = {
  minHeight: 72,
  display: 'grid',
  gridTemplateColumns: '56px minmax(0, 1fr) 56px',
  alignItems: 'stretch',
  gap: 'var(--space-2)',
};

const lengthEditorControlValueStyle: CSSProperties = {
  minWidth: 0,
  display: 'grid',
  placeItems: 'center',
  gap: '3px',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-2)',
};

const lengthEditorControlLabelStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: '0.68rem',
  fontWeight: 850,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const lengthEditorControlNumberStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-2xl)',
  fontWeight: 900,
  lineHeight: 1,
  whiteSpace: 'nowrap',
};

const lengthEditorStepButtonStyle: CSSProperties = {
  minWidth: 56,
  minHeight: 72,
  display: 'grid',
  placeItems: 'center',
  color: 'white',
  background: 'var(--color-blue)',
  border: '1px solid var(--color-blue-deep)',
  borderRadius: 'var(--radius-lg)',
  fontSize: 'var(--text-3xl)',
  fontWeight: 900,
  lineHeight: 1,
};

const lengthEditorStepButtonDisabledStyle: CSSProperties = {
  ...lengthEditorStepButtonStyle,
  color: 'var(--color-text-muted)',
  background: 'var(--color-bg)',
  borderColor: 'var(--color-border)',
  opacity: 0.42,
};

const lengthEditorManualStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-1)',
};

const lengthEditorInputStyle: CSSProperties = {
  ...measurementInputStyle,
  minHeight: 48,
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-3)',
};

const lengthEditorDoneButtonStyle: CSSProperties = {
  minHeight: 50,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--color-text-on-orange)',
  background: 'var(--color-accent)',
  border: '1px solid var(--color-accent)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-base)',
  fontWeight: 900,
};

const angleNudgeButtonStyle: CSSProperties = {
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-accent)',
  fontWeight: 850,
  padding: 'var(--space-2)',
};

const offsetShellStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-3)',
};

const offsetSelectorRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 0.9fr) minmax(0, 0.7fr)',
  gap: 'var(--space-2)',
};

const offsetChipButtonStyle: CSSProperties = {
  minWidth: 0,
  minHeight: 54,
  background: 'var(--color-blue)',
  border: '1px solid var(--color-blue-deep)',
  borderRadius: 'var(--radius-md)',
  color: 'white',
  padding: 'var(--space-2) var(--space-3)',
  display: 'grid',
  gap: '2px',
  justifyItems: 'start',
  textAlign: 'left',
  boxShadow: '0 10px 24px rgba(0, 122, 255, 0.22)',
};

const offsetChipLabelStyle: CSSProperties = {
  fontSize: '0.62rem',
  fontWeight: 850,
  opacity: 0.72,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const offsetChipValueStyle: CSSProperties = {
  fontSize: 'var(--text-sm)',
  fontWeight: 900,
  lineHeight: 1.05,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
};

const offsetQuickInputGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--space-2)',
};

const parallelSecondaryInputGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--space-2)',
  marginTop: 'var(--space-3)',
};

const offsetDiagramFrameStyle: CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-3)',
  boxShadow: 'var(--shadow-card)',
};

const offsetDiagramHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: 'var(--color-text-muted)',
  fontSize: '0.68rem',
  fontWeight: 850,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: 'var(--space-2)',
};

const offsetResultGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--space-2)',
};

const rollingSetupStyle: CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-3)',
  boxShadow: 'var(--shadow-card)',
};

const rollingSetupTitleStyle: CSSProperties = {
  fontSize: 'var(--text-lg)',
  lineHeight: 1.1,
  fontWeight: 900,
  letterSpacing: 0,
  marginBottom: 'var(--space-3)',
};

const rollingSetupGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--space-2)',
};

const rollingSetupMetricStyle: CSSProperties = {
  minHeight: 68,
  display: 'grid',
  alignContent: 'center',
  gap: 'var(--space-1)',
  padding: 'var(--space-3)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
};

const rollingSetupMetricFeaturedStyle: CSSProperties = {
  ...rollingSetupMetricStyle,
  background: 'var(--color-blue)',
  borderColor: 'var(--color-blue)',
  color: 'white',
};

const rollingSetupMetricLabelStyle: CSSProperties = {
  color: 'inherit',
  opacity: 0.78,
  fontSize: '0.68rem',
  fontWeight: 850,
  textTransform: 'uppercase',
};

const rollingSetupMetricValueStyle: CSSProperties = {
  color: 'inherit',
  fontSize: 'clamp(1.25rem, 6vw, 1.8rem)',
  fontWeight: 900,
  lineHeight: 1,
  whiteSpace: 'nowrap',
};

const gainEstimateNoteStyle: CSSProperties = {
  marginTop: 'var(--space-2)',
  color: 'var(--color-text-muted)',
  fontSize: 'var(--text-xs)',
  fontWeight: 750,
  lineHeight: 1.35,
};

const parallelRunMarksStyle: CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-3)',
  boxShadow: 'var(--shadow-card)',
};

const parallelRunMarksHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
  marginBottom: 'var(--space-3)',
};

const parallelRunMarksTitleStyle: CSSProperties = {
  fontSize: 'var(--text-lg)',
  lineHeight: 1.1,
  fontWeight: 900,
  letterSpacing: 0,
};

const parallelRunMarksSubStyle: CSSProperties = {
  color: 'var(--color-accent)',
  fontSize: 'var(--text-sm)',
  fontWeight: 900,
  marginTop: 'var(--space-1)',
};

const parallelRunMetaStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: 'var(--text-xs)',
  fontWeight: 850,
  textAlign: 'right',
  whiteSpace: 'nowrap',
};

const parallelRunListStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
};

const parallelRunRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '76px minmax(0, 1fr) minmax(0, 1fr)',
  alignItems: 'center',
  gap: 'var(--space-2)',
  minHeight: 48,
  padding: 'var(--space-2) var(--space-3)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
};

const parallelRunNameStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-sm)',
  fontWeight: 900,
};

const parallelRunMarksValueStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-base)',
  fontWeight: 900,
  textAlign: 'right',
  whiteSpace: 'nowrap',
};

const parallelRunFootStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: 'var(--text-xs)',
  fontWeight: 750,
  marginTop: 'var(--space-3)',
};

const resultActionBarStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
};

const resultShareButtonStyle: CSSProperties = {
  width: '100%',
  minHeight: 48,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-2)',
  color: 'var(--color-blue)',
  background: 'var(--color-blue-soft)',
  border: '1px solid rgba(0, 122, 255, 0.28)',
  borderRadius: 'var(--radius-md)',
  fontWeight: 900,
};

const resultShareStatusStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-xs)',
  fontWeight: 800,
  textAlign: 'center',
};

const offsetAnglePanelStyle: CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-3)',
  boxShadow: 'var(--shadow-card)',
};

const saddleAngleHelperStyle: CSSProperties = {
  marginBottom: 'var(--space-2)',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-xs)',
  fontWeight: 850,
  letterSpacing: '0.02em',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
  textAlign: 'center',
};

const angleLevelLinkStyle: CSSProperties = {
  minHeight: 28,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--color-blue)',
  background: 'var(--color-blue-soft)',
  border: '1px solid rgba(0, 122, 255, 0.28)',
  borderRadius: 'var(--radius-pill)',
  padding: '0 var(--space-3)',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
  textDecoration: 'none',
};

const squareAlignLinkStyle: CSSProperties = {
  ...angleLevelLinkStyle,
  color: 'var(--color-accent)',
  background: 'var(--color-accent-soft)',
  border: '1px solid rgba(255, 106, 26, 0.32)',
};

function clampAngle(value: number, min = 5, max = 89): number {
  return Math.max(min, Math.min(max, value));
}

function formatBenderLabel(selection: BenderSelection): string {
  const model = listBenders().find((bender) => bender.id === selection.benderId);
  if (!model) return selection.benderId;
  if (model.manufacturer === 'Greenlee' && model.model === '555') return 'GRNL 555';
  if (model.manufacturer === 'Greenlee' && model.model.includes('Hand'))
    return 'Greenlee Site-Rite';
  return `${model.manufacturer} ${model.model}`;
}

function roundAngle(value: number): number {
  return Math.round(value * 10) / 10;
}

function clampNumber(value: number, min?: number, max?: number): number {
  const aboveMin = min === undefined ? value : Math.max(min, value);
  return max === undefined ? aboveMin : Math.min(max, aboveMin);
}

function levelTargetPath(angle: number, returnTo: string): string {
  const params = new URLSearchParams({
    target: formatNumberForInput(roundAngle(angle)),
    returnTo,
  });
  return `/level?${params.toString()}`;
}

function squareAlignPath(returnTo: string, target = 0): string {
  const params = new URLSearchParams({
    target: formatNumberForInput(roundAngle(target)),
    returnTo,
  });
  return `/square-align?${params.toString()}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function shortInputLabel(spec: InputSpec): string {
  if (spec.key === 'fromEnd' || spec.key === 'startMark') return 'From end';
  if (spec.key === 'centerMark') return 'Center mark';
  if (spec.key === 'obstructionHeight') return 'Height';
  if (spec.key === 'obstructionWidth') return 'Width';
  return spec.label;
}

function formatMeasurementValue(
  value: number,
  unit: string,
  lengthUnit: LengthDisplayUnit,
): string {
  if (unit === 'in') return formatInches(value, { lengthUnit });
  if (unit === '°') return `${formatNumberForInput(value)}\u00b0`;
  return unit ? `${formatNumberForInput(value)} ${unit}` : formatNumberForInput(value);
}

function formatEditableMeasurementValue(
  value: number,
  spec: InputSpec,
  lengthUnit: LengthDisplayUnit,
): string {
  if (spec.type.kind === 'number' && spec.type.unit === 'in' && lengthUnit === 'Centimeters') {
    return formatNumberForInput(value * 2.54);
  }
  return formatNumberForInput(value);
}

function formatFractionValue(value: number): string {
  const whole = Math.floor(Math.max(0, value));
  const fraction = Math.max(0, value - whole);
  return fraction < 0.001 ? '0' : formatInches(fraction);
}

function parseEditableMeasurementValue(
  value: string,
  spec: InputSpec,
  lengthUnit: LengthDisplayUnit,
): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (spec.type.kind === 'number' && spec.type.unit === 'in') {
    if (lengthUnit === 'Centimeters') {
      const centimeters = Number(trimmed);
      return Number.isFinite(centimeters) ? centimeters / 2.54 : null;
    }
    return parseInchesInput(trimmed);
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function measurementUnitSuffix(
  unit: string,
  lengthUnit: LengthDisplayUnit,
  isEditing: boolean,
): string {
  if (unit !== 'in') return unit ? ` (${unit})` : '';
  if (lengthUnit === 'Centimeters') return ' (cm)';
  if (isEditing && lengthUnit === 'Feet + inches') return ' (in)';
  return '';
}

function formatNumberForInput(value: number): string {
  if (!Number.isFinite(value)) return '';
  return Number.isInteger(value) ? value.toFixed(0) : String(Number(value.toFixed(3)));
}

function formatAngle(angle: number): string {
  return `${Number.isInteger(angle) ? angle.toFixed(0) : angle.toFixed(1)}\u00b0`;
}

function formatAngleIncrement(angle: number): string {
  return formatAngle(angle);
}

interface Field {
  label: string;
  value: number;
  unit?: 'in' | 'deg' | 'count';
  primary?: boolean;
}

function renderFieldValue(field: Field): string {
  if (field.unit === 'deg') return formatAngle(field.value);
  if (field.unit === 'count') return String(Math.round(field.value));
  return formatInches(field.value);
}

function hasVerifiedGain(shoe: { verifiedFields?: readonly string[] } | null | undefined): boolean {
  return shoe?.verifiedFields?.includes('gain') ?? false;
}

function gainBasedLengthLabel(label: 'Cut length' | 'Total length', gainVerified: boolean): string {
  if (gainVerified) return label;
  return label === 'Cut length' ? 'Est. cut' : 'Est. total';
}

function resultToFields(
  type: string,
  result: unknown,
  inputs: Record<string, number>,
  gainVerified = true,
): Field[] {
  if (!result || typeof result !== 'object') return [];
  const r = result as Record<string, number>;
  switch (type) {
    case 'ninety':
      return [
        { label: 'Mark from end', value: r.markFromEnd!, primary: true },
        { label: gainBasedLengthLabel('Total length', gainVerified), value: r.totalLength! },
      ];
    case 'back-to-back':
      return [
        { label: 'First mark', value: r.firstMark!, primary: true },
        { label: 'Second mark', value: r.secondMark! },
        { label: gainBasedLengthLabel('Total length', gainVerified), value: r.totalLength! },
      ];
    case 'offset': {
      const firstMark = inputs.fromEnd ?? 0;
      const secondMark = firstMark + r.distanceBetweenMarks!;
      return [
        { label: 'Distance between bends', value: r.distanceBetweenMarks!, primary: true },
        { label: 'Mark 1', value: firstMark },
        { label: 'Mark 2', value: secondMark },
        { label: 'Shrink', value: r.shrink! },
      ];
    }
    case 'saddle3':
      return [
        { label: '1st side mark', value: r.firstSideMark!, primary: true },
        { label: 'Center mark', value: r.centerMark! },
        { label: '2nd side mark', value: r.secondSideMark! },
        { label: 'Center bend', value: r.centerAngle!, unit: 'deg' },
        { label: 'Side bends', value: r.sideBendAngle!, unit: 'deg' },
        { label: 'Shrink', value: r.shrink! },
      ];
    case 'saddle4': {
      const marks = (r.marks as unknown as number[]) ?? [];
      const labels = ['First mark', 'Second mark', 'Third mark', 'Fourth mark'];
      return [
        ...marks.map((m, i) => ({
          label: labels[i] ?? `Mark ${i + 1}`,
          value: m,
          primary: i === 0,
        })),
        { label: 'Shrink', value: r.shrink! },
      ];
    }
    case 'kick':
      return [
        { label: '90 bend mark', value: r.ninetyMark!, primary: true },
        { label: 'Kick mark from 90', value: r.kickMarkDistance! },
        { label: 'Shrink', value: r.shrink! },
        { label: gainBasedLengthLabel('Cut length', gainVerified), value: r.totalLength! },
      ];
    case 'rolling-offset':
      return [
        { label: 'Between bends', value: r.distanceBetweenMarks!, primary: true },
        { label: 'True offset', value: r.trueOffset! },
        { label: 'Roll conduit', value: r.rollAngle!, unit: 'deg' },
        { label: 'Shrink', value: r.shrink! },
      ];
    case 'parallel-offset': {
      const runs =
        (
          r as unknown as {
            runs: { run: number; firstMark: number; secondMark: number; markShift: number }[];
          }
        ).runs ?? [];
      return [
        { label: 'Each add', value: r.progression!, primary: true },
        { label: 'Between bends', value: r.distanceBetweenMarks! },
        { label: 'Shrink', value: r.shrink! },
        ...runs.flatMap((run) => [
          { label: `Run ${run.run} mark 1`, value: run.firstMark },
          { label: `Run ${run.run} mark 2`, value: run.secondMark },
        ]),
      ];
    }
    case 'concentric': {
      const parallels =
        (r as unknown as { parallels: { radius: number; additionalLength: number }[] }).parallels ??
        [];
      if (parallels.length === 0) return [];
      const outermost = parallels[parallels.length - 1]!;
      return [
        { label: 'Outermost add', value: outermost.additionalLength, primary: true },
        ...parallels.slice(0, -1).map((parallel, index) => ({
          label: index === 0 ? 'Inner run add' : `Run ${index + 1} add`,
          value: parallel.additionalLength,
        })),
      ];
    }
    case 'segmented':
      return [
        { label: 'Segment count', value: r.segmentCount!, unit: 'count', primary: true },
        { label: 'Chord length', value: r.chordLength! },
        { label: 'Total length', value: r.totalLength! },
      ];
    default:
      return [];
  }
}
